/**
 * Wishlist functionality for IsAlbi B2B Theme
 * Handles wishlist CRUD operations, item management, and UI interactions
 */

// Safari/iOS Performance: Prevent duplicate script execution
if (window.__wishlistScriptLoaded) {
  // Script already loaded, exit early
  throw new Error('Wishlist script already loaded');
}
window.__wishlistScriptLoaded = true;

(function() {
  'use strict';
  
  // Development mode check - only log in development
  const isDev = window.location.hostname === 'localhost' || 
                window.location.hostname.includes('.myshopify.com') ||
                window.location.search.includes('debug=true');
  
  // Conditional logging helper - only logs in development mode
  const devLog = (...args) => {
    if (isDev) {
      console.log(...args);
    }
  };

  // Configuration
  const API_URL = 'https://isalbi.harborlight.app/api/wishlists';
  const SHOP_DOMAIN = window.Shopify?.shop || 'isalbi-b2b.myshopify.com';
  const CUSTOMER_ID = window.Shopify?.customer?.id || null;
  const moqMemoizer = (window.__isalbiMoqUtils && typeof window.__isalbiMoqUtils.createMoqMemoizer === 'function')
    ? window.__isalbiMoqUtils.createMoqMemoizer({ ttlMs: 30000, maxEntries: 500 })
    : null;

  // Utility functions
  function getApiUrl(path = '', params = {}) {
    // Ensure base URL doesn't have trailing slash
    let baseUrl = API_URL.endsWith('/') ? API_URL.slice(0, -1) : API_URL;
    
    // Handle path - remove leading slash if present and append to base URL
    let fullPath = baseUrl;
    if (path) {
      // Remove leading slash to avoid issues with new URL() constructor
      const cleanPath = path.startsWith('/') ? path.slice(1) : path;
      fullPath = baseUrl + '/' + cleanPath;
    }
    
    const url = new URL(fullPath);
    params.shop = params.shop || SHOP_DOMAIN;
    Object.keys(params).forEach(key => {
      if (params[key] !== null && params[key] !== undefined && params[key] !== '') {
        url.searchParams.append(key, String(params[key]));
      }
    });
    
    return url.toString();
  }

  function getMoqUtils() {
    return window.__isalbiMoqUtils || null;
  }

  function getEffectiveMoq(rawMoq) {
    const cacheKey = `${rawMoq || '1'}:${window.__isalbiCompanyOverride?.moq || ''}`;
    if (moqMemoizer && typeof moqMemoizer.get === 'function') {
      return moqMemoizer.get(cacheKey, () => computeEffectiveMoq(rawMoq));
    }
    return computeEffectiveMoq(rawMoq);
  }

  function computeEffectiveMoq(rawMoq) {
    const moqUtils = getMoqUtils();
    const productMoq = parseInt(rawMoq, 10);
    const overrideMoq = moqUtils && typeof moqUtils.getGlobalOverrideMoq === 'function'
      ? moqUtils.getGlobalOverrideMoq()
      : null;
    if (moqUtils && typeof moqUtils.resolveEffectiveMoq === 'function') {
      return moqUtils.resolveEffectiveMoq({ overrideMoq, productMoq, fallback: 1 });
    }
    if (Number.isFinite(overrideMoq) && overrideMoq > 0) return overrideMoq;
    return Number.isFinite(productMoq) && productMoq > 0 ? productMoq : 1;
  }

  function normalizeQtyWithMoq(quantity, moq) {
    const moqUtils = getMoqUtils();
    if (moqUtils && typeof moqUtils.normalizeQuantityToMoq === 'function') {
      return moqUtils.normalizeQuantityToMoq(quantity, moq);
    }
    const parsedMoq = Number.isFinite(parseInt(moq, 10)) && parseInt(moq, 10) > 0 ? parseInt(moq, 10) : 1;
    const parsedQty = Number.isFinite(parseInt(quantity, 10)) ? parseInt(quantity, 10) : parsedMoq;
    const normalized = Math.max(parsedMoq, Math.ceil(parsedQty / parsedMoq) * parsedMoq);
    return { quantity: normalized, moq: parsedMoq, adjusted: normalized !== parsedQty };
  }

  document.addEventListener('company-order-override:updated', () => {
    if (moqMemoizer && typeof moqMemoizer.clear === 'function') {
      moqMemoizer.clear();
    }
  });

  const WISHLIST_STOCK_BADGES = {
    in_stock: { label: 'In Stock', cls: 'soh-badge--instock' },
    pre_order: { label: 'Pre-order', cls: 'soh-badge--preorder' }
  };

  /**
   * Resolves the stock badge for a wishlist row.
   *
   * Uses the same rule as the PDP and product cards (assets/product-inventory.js,
   * snippets/product-card.liquid): stock below one MOQ worth of units with an ETA is a
   * pre-order. Falls back to the line-item stock type when the inventory count is not
   * available, and shows no badge when the state cannot be determined.
   *
   * @returns {{label: string, cls: string}|null}
   */
  function resolveWishlistStockBadge({ inventoryQuantity, eta, moq, stockType, thresholdMultiplier } = {}) {
    const hasEta = String(eta == null ? '' : eta).trim() !== '';

    const parsedMoq = parseInt(moq, 10);
    const effectiveMoq = Number.isFinite(parsedMoq) && parsedMoq > 0 ? parsedMoq : 1;

    const parsedMultiplier = parseInt(thresholdMultiplier, 10);
    const multiplier = Number.isFinite(parsedMultiplier) && parsedMultiplier >= 0 ? parsedMultiplier : 1;
    const outOfStockThreshold = effectiveMoq * multiplier;

    const parsedQty = parseInt(inventoryQuantity, 10);
    if (Number.isFinite(parsedQty)) {
      if (parsedQty < outOfStockThreshold) {
        return hasEta ? WISHLIST_STOCK_BADGES.pre_order : null;
      }
      return WISHLIST_STOCK_BADGES.in_stock;
    }

    const normalizedStockType = String(stockType || '').trim();
    if (normalizedStockType === 'backorder' || normalizedStockType === 'forward') {
      return WISHLIST_STOCK_BADGES.pre_order;
    }
    if (normalizedStockType === 'available') {
      return WISHLIST_STOCK_BADGES.in_stock;
    }
    return null;
  }

  window.__isalbiWishlistStockBadge = { resolveWishlistStockBadge };

  function triggerHeaderWishlistPulse() {
  const headerIcon = document.getElementById('header-wishlist-icon');
  if (!headerIcon) {
    return;
  }
  
  // Remove any existing classes
  headerIcon.classList.remove('is-pulsing', 'is-active');
  
  // Trigger reflow to restart animation
  void headerIcon.offsetWidth;
  
  // Add pulsing class for the initial animation
  headerIcon.classList.add('is-pulsing');
  
  // After animation completes, switch to active state with continuous pulse
  setTimeout(() => {
    headerIcon.classList.remove('is-pulsing');
    headerIcon.classList.add('is-active');
  }, 1500); // Match animation duration
}

function showNotification(message, type = 'success') {
    // Create a simple notification
    const notification = document.createElement('div');
    let backgroundColor = '#008060'; // success (green)
    if (type === 'error') {
      backgroundColor = '#BF0711'; // error (red)
    } else if (type === 'warning') {
      backgroundColor = '#FFC107'; // warning (yellow/amber)
    } else if (type === 'info') {
      backgroundColor = '#0D6EFD'; // info (blue)
    }
    
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 1rem 1.5rem;
      background: ${backgroundColor};
      color: ${type === 'warning' ? '#000000' : '#FFFFFF'};
      border-radius: 6px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      z-index: 10000;
      font-size: 0.875rem;
      max-width: 400px;
    `;
    notification.textContent = message;
    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.opacity = '0';
      notification.style.transition = 'opacity 0.3s';
      setTimeout(() => notification.remove(), 300);
    }, 3000);
  }

  function showConfirmModal(title, message) {
    return new Promise((resolve) => {
      const modal = document.getElementById('wishlist-confirm-modal');
      if (!modal) {
        console.error('[Wishlist] Confirmation modal not found');
        resolve(false);
        return;
      }

      const titleEl = modal.querySelector('#wishlist-confirm-modal-title');
      const messageEl = modal.querySelector('#wishlist-confirm-modal-message');
      const cancelBtn = modal.querySelector('.js-wishlist-confirm-cancel');
      const okBtn = modal.querySelector('.js-wishlist-confirm-ok');
      const overlay = modal.querySelector('.wishlist-confirm-modal__overlay');

      if (titleEl) titleEl.textContent = title || 'Confirm';
      if (messageEl) messageEl.textContent = message || 'Are you sure?';

      // Remove existing listeners by cloning and replacing buttons
      const newCancelBtn = cancelBtn.cloneNode(true);
      const newOkBtn = okBtn.cloneNode(true);
      cancelBtn.parentNode.replaceChild(newCancelBtn, cancelBtn);
      okBtn.parentNode.replaceChild(newOkBtn, okBtn);

      // Close handler
      const closeModal = (confirmed) => {
        modal.classList.add('hidden');
        resolve(confirmed);
      };

      // Add event listeners
      newCancelBtn.addEventListener('click', () => closeModal(false));
      newOkBtn.addEventListener('click', () => closeModal(true));
      if (overlay) {
        overlay.addEventListener('click', () => closeModal(false));
      }

      // ESC key handler
      const escHandler = (e) => {
        if (e.key === 'Escape') {
          closeModal(false);
          document.removeEventListener('keydown', escHandler);
        }
      };
      document.addEventListener('keydown', escHandler);

      // Show modal
      modal.classList.remove('hidden');
    });
  }

  // Wishlist List Management
  class WishlistList {
    constructor(container) {
      this.container = container;
      this.customerId = container.dataset.customerId || CUSTOMER_ID;
      this.shopDomain = container.dataset.shopDomain || SHOP_DOMAIN;
      this.apiUrl = container.dataset.apiUrl || API_URL;
      this.wishlists = [];

      // Always initialize - errors will be shown after timeout if loading fails
      this.init();
    }

    async init() {
      await this.loadWishlists();
      this.setupEventListeners();
    }

    async loadWishlists() {
      const loadingEl = this.container.querySelector('.wishlist-list__loading');
      const emptyEl = this.container.querySelector('.wishlist-list__empty');
      const gridEl = this.container.querySelector('.wishlist-list__grid');

      if (!loadingEl) {
        console.error('[Wishlist] Loading element not found');
        return;
      }

      // Set up error timeout (5 seconds)
      let hasCompleted = false;
      const errorTimeout = setTimeout(() => {
        if (!hasCompleted) {
          hasCompleted = true; // Prevent error from showing if load completes
          
          // Check for missing customer ID
          if (!this.customerId) {
            console.warn('[Wishlist] Customer ID not found');
            loadingEl.innerHTML = '<p style="color: #BF0711;">Please log in to view wishlists.</p>';
            return;
          }

          // Generic loading timeout error
          loadingEl.innerHTML = '<p style="color: #BF0711;">Loading is taking longer than expected. Please refresh the page or check your connection.</p>';
        }
      }, 5000);

      console.log('[Wishlist] Loading wishlists for customer:', this.customerId);

      try {
        // Validate customer ID before attempting to load
        if (!this.customerId) {
          // Wait for timeout to show error
          return;
        }

        const url = getApiUrl('', { customer_id: this.customerId });
        console.log('[Wishlist] Fetching from URL:', url);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include'
        });
        console.log('[Wishlist] Response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[Wishlist] API error response:', errorText);
          throw new Error(`Failed to load wishlists: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();
        console.log('[Wishlist] API response data:', data);
        
        // The API returns an array directly for list requests
        if (Array.isArray(data)) {
          this.wishlists = data;
        } else if (data && typeof data === 'object') {
          // Handle wrapped responses
          if (Array.isArray(data.wishlists)) {
            this.wishlists = data.wishlists;
          } else if (Array.isArray(data.data)) {
            this.wishlists = data.data;
          } else {
            console.warn('[Wishlist] Unexpected response format - expected array:', data);
            this.wishlists = [];
          }
        } else {
          console.warn('[Wishlist] Unexpected response type:', typeof data, data);
          this.wishlists = [];
        }

        hasCompleted = true;
        clearTimeout(errorTimeout);

        if (loadingEl) loadingEl.style.display = 'none';

        if (this.wishlists.length === 0) {
          console.log('[Wishlist] No wishlists found, showing empty state');
          if (emptyEl) emptyEl.classList.remove('hidden');
          if (gridEl) gridEl.style.display = 'none';
        } else {
          console.log('[Wishlist] Found', this.wishlists.length, 'wishlist(s)');
          if (emptyEl) emptyEl.classList.add('hidden');
          if (gridEl) {
            gridEl.style.display = 'grid';
            this.renderWishlists();
          }
        }
      } catch (error) {
        hasCompleted = true;
        clearTimeout(errorTimeout);
        console.error('[Wishlist] Error loading wishlists:', error);
        console.error('[Wishlist] Error details:', {
          message: error.message,
          stack: error.stack,
          name: error.name
        });
        
        if (loadingEl) {
          let errorMessage = 'Error loading wishlists. ';
          
          if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError')) {
            errorMessage += 'Network error - the API may be unavailable or there may be a CORS issue. Please check the console for details.';
          } else if (error.message.includes('404')) {
            errorMessage += 'API endpoint not found. Please verify the API URL is correct.';
          } else if (error.message.includes('403') || error.message.includes('401')) {
            errorMessage += 'Authentication error. Please ensure you are logged in.';
          } else {
            errorMessage += error.message;
          }
          
          loadingEl.innerHTML = `<p style="color: #BF0711;">${errorMessage}</p>`;
        }
      }
    }

    renderWishlists() {
      const gridEl = this.container.querySelector('.wishlist-list__grid');
      gridEl.innerHTML = this.wishlists.map(wishlist => this.renderWishlistCard(wishlist)).join('');
      
      // Setup event listeners for cards
      gridEl.querySelectorAll('.wishlist-card').forEach(card => {
        const wishlistId = card.dataset.wishlistId;
        card.addEventListener('click', (e) => {
          if (!e.target.closest('.wishlist-card__actions')) {
            window.location.href = `/pages/wishlist?id=${wishlistId}`;
          }
        });
      });

      // Setup delete buttons
      gridEl.querySelectorAll('.js-wishlist-delete').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.stopPropagation();
          const wishlistId = btn.dataset.wishlistId;
          const confirmed = await showConfirmModal(
            'Delete Wishlist',
            'Are you sure you want to delete this wishlist?'
          );
          if (confirmed) {
            await this.deleteWishlist(wishlistId);
          }
        });
      });

      // Setup share buttons
      gridEl.querySelectorAll('.js-wishlist-share').forEach(btn => {
        btn.addEventListener('click', async (e) => {
          e.stopPropagation();
          const wishlistId = btn.dataset.wishlistId;
          await this.shareWishlist(wishlistId);
        });
      });
    }

    renderWishlistCard(wishlist) {
      const shareUrl = wishlist.publicShareToken 
        ? `${window.location.origin}/pages/wishlist-share?token=${wishlist.publicShareToken}`
        : null;

      // Get date value - prefer updatedAt, fallback to createdAt
      const dateValue = wishlist.updatedAt || wishlist.createdAt;
      const formattedDate = this.formatDate(dateValue);

      return `
        <div class="wishlist-card" data-wishlist-id="${wishlist.id}">
          <div class="wishlist-card__header">
            <h3 class="wishlist-card__title">${this.escapeHtml(wishlist.name)}</h3>
            <div class="wishlist-card__actions">
              ${shareUrl ? `
                <button type="button" class="wishlist-card__action-btn js-wishlist-share" data-wishlist-id="${wishlist.id}" title="Share">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M15 8a3 3 0 10-2.977-2.63l-4.94 2.47a3 3 0 100 4.319l4.94 2.47a3 3 0 10.895-1.789l-4.94-2.47a3.027 3.027 0 000-.74l4.94-2.47C13.456 7.68 14.19 8 15 8z"/>
                  </svg>
                </button>
              ` : ''}
              <button type="button" class="wishlist-card__action-btn js-wishlist-delete" data-wishlist-id="${wishlist.id}" title="Delete">
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
              </button>
            </div>
          </div>
          ${wishlist.description ? `<p class="wishlist-card__description">${this.escapeHtml(wishlist.description)}</p>` : '<p class="wishlist-card__description" style="opacity: 0.5;">No description</p>'}
          <div class="wishlist-card__meta">
            <span>${wishlist.itemCount || 0} ${(wishlist.itemCount || 0) === 1 ? 'item' : 'items'}</span>
            ${formattedDate ? `<span>${formattedDate}</span>` : ''}
          </div>
        </div>
      `;
    }

    async deleteWishlist(wishlistId) {
      try {
        const response = await fetch(getApiUrl(`/${wishlistId}`), {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ customerId: this.customerId })
        });

        if (!response.ok) throw new Error('Failed to delete wishlist');

        showNotification('Wishlist deleted successfully');
        await this.loadWishlists();
      } catch (error) {
        console.error('[Wishlist] Error deleting wishlist:', error);
        showNotification('Error deleting wishlist', 'error');
      }
    }

    async shareWishlist(wishlistId) {
      try {
        const wishlist = this.wishlists.find(w => w.id === wishlistId);
        if (!wishlist) return;

        // If already public, copy link
        if (wishlist.publicShareToken) {
          const shareUrl = `${window.location.origin}/pages/wishlist-share?token=${wishlist.publicShareToken}`;
          navigator.clipboard.writeText(shareUrl);
          showNotification('Share link copied to clipboard!');
          return;
        }

        // Make wishlist public
        const response = await fetch(getApiUrl(`/${wishlistId}`), {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            customerId: this.customerId,
            isPublic: true
          })
        });

        if (!response.ok) throw new Error('Failed to share wishlist');

        const updated = await response.json();
        const shareUrl = `${window.location.origin}/pages/wishlist-share?token=${updated.publicShareToken}`;
        navigator.clipboard.writeText(shareUrl);
        showNotification('Share link copied to clipboard!');
        await this.loadWishlists();
      } catch (error) {
        console.error('[Wishlist] Error sharing wishlist:', error);
        showNotification('Error sharing wishlist', 'error');
      }
    }

    setupEventListeners() {
      // Create wishlist button
      const createBtn = document.querySelector('.js-create-wishlist');
      if (createBtn) {
        createBtn.addEventListener('click', () => {
          const modal = document.getElementById('wishlist-create-modal');
          if (modal) modal.classList.remove('hidden');
        });
      }

      // Modal close
      const modal = document.getElementById('wishlist-create-modal');
      if (modal) {
        const closeBtn = modal.querySelector('.wishlist-create-modal__close');
        const cancelBtn = modal.querySelector('.js-wishlist-create-cancel');
        const overlay = modal.querySelector('.wishlist-create-modal__overlay');

        [closeBtn, cancelBtn, overlay].forEach(el => {
          if (el) el.addEventListener('click', () => modal.classList.add('hidden'));
        });
      }

      // Create form submission (prevent duplicate listeners)
      const form = document.querySelector('.js-wishlist-create-form');
      if (form && !form.dataset.listenerAdded) {
        form.dataset.listenerAdded = 'true';
        let isSubmitting = false; // Track submission state
        
        form.addEventListener('submit', async (e) => {
          e.preventDefault();
          e.stopPropagation(); // Prevent event bubbling
          
          // Prevent double submission
          if (isSubmitting) {
            console.warn('[Wishlist] Form submission already in progress, ignoring duplicate submit');
            return;
          }
          
          const submitBtn = form.querySelector('button[type="submit"]');
          if (submitBtn && submitBtn.disabled) {
            console.warn('[Wishlist] Submit button already disabled, ignoring submit');
            return; // Already submitting
          }
          
          const formData = new FormData(form);
          const name = formData.get('name')?.trim();
          const description = formData.get('description')?.trim() || null;

          if (!name) {
            showNotification('Please enter a wishlist name', 'error');
            return;
          }

          // Set submitting flag and disable submit button to prevent double submission
          isSubmitting = true;
          if (submitBtn) {
            submitBtn.disabled = true;
            const originalText = submitBtn.textContent;
            submitBtn.textContent = 'Creating...';
            
            try {
              // Generate idempotency key to prevent duplicate creation
              const idempotencyKey = `wishlist-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
              
              const response = await fetch(getApiUrl(''), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  customerId: this.customerId,
                  name,
                  description,
                  idempotencyKey
                })
              });

              if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to create wishlist');
              }

              const wishlist = await response.json();
              showNotification('Wishlist created successfully!');
              modal.classList.add('hidden');
              form.reset();
              await this.loadWishlists();

              // Redirect to wishlist detail page using humanReadableId if available, otherwise use id
              const redirectId = wishlist.humanReadableId || wishlist.id;
              
              // Clear submitting flag after successful creation
              isSubmitting = false;
              
              setTimeout(() => {
                window.location.href = `/pages/wishlist?id=${redirectId}`;
              }, 500);
            } catch (error) {
              console.error('[Wishlist] Error creating wishlist:', error);
              showNotification(error.message || 'Error creating wishlist', 'error');
              
              // Re-enable submit button on error and clear submitting flag
              isSubmitting = false;
              submitBtn.disabled = false;
              submitBtn.textContent = originalText;
            }
          } else {
            // Fallback if submit button not found
            isSubmitting = true;
            try {
              // Generate idempotency key to prevent duplicate creation
              const idempotencyKey = `wishlist-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
              
              const response = await fetch(getApiUrl(''), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                  customerId: this.customerId,
                  name,
                  description,
                  idempotencyKey
                })
              });

              if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || 'Failed to create wishlist');
              }

              const wishlist = await response.json();
              showNotification('Wishlist created successfully!');
              modal.classList.add('hidden');
              form.reset();
              await this.loadWishlists();

              // Redirect to wishlist detail page using humanReadableId if available, otherwise use id
              const redirectId = wishlist.humanReadableId || wishlist.id;
              
              // Clear submitting flag
              isSubmitting = false;
              
              setTimeout(() => {
                window.location.href = `/pages/wishlist?id=${redirectId}`;
              }, 500);
            } catch (error) {
              console.error('[Wishlist] Error creating wishlist:', error);
              showNotification(error.message || 'Error creating wishlist', 'error');
              
              // Clear submitting flag on error
              isSubmitting = false;
            }
          }
        });
      }

      // CSV import button
      const csvImportBtn = document.querySelector('.js-csv-import');
      if (csvImportBtn) {
        csvImportBtn.addEventListener('click', () => {
          const modal = document.getElementById('wishlist-csv-import-modal');
          if (modal) modal.classList.remove('hidden');
        });
      }

      // CSV template download
      const csvTemplateBtn = document.querySelector('.js-csv-download-template');
      if (csvTemplateBtn) {
        csvTemplateBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.downloadCSVTemplate();
        });
      }

      // CSV import modal close
      const csvImportModal = document.getElementById('wishlist-csv-import-modal');
      if (csvImportModal) {
        const closeBtn = csvImportModal.querySelector('.wishlist-csv-import-modal__close');
        const cancelBtn = csvImportModal.querySelector('.js-csv-import-cancel');
        const overlay = csvImportModal.querySelector('.wishlist-csv-import-modal__overlay');

        [closeBtn, cancelBtn, overlay].forEach(el => {
          if (el) el.addEventListener('click', () => csvImportModal.classList.add('hidden'));
        });
      }

      // CSV import form submission
      const csvImportForm = document.querySelector('.js-csv-import-form');
      if (csvImportForm && !csvImportForm.dataset.listenerAdded) {
        csvImportForm.dataset.listenerAdded = 'true';
        csvImportForm.addEventListener('submit', async (e) => {
          e.preventDefault();
          await this.handleCSVImport(csvImportForm);
        });
      }

      // Export all wishlists button (prevent duplicate listeners)
      const exportAllBtn = document.querySelector('.js-export-all-wishlists');
      if (exportAllBtn && !exportAllBtn.dataset.listenerAdded) {
        exportAllBtn.dataset.listenerAdded = 'true';
        exportAllBtn.addEventListener('click', async () => {
          await this.exportAllWishlistsToCSV();
        });
      }
    }

    escapeCSVField(str) {
      if (str === null || str === undefined) return '';
      str = String(str);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    }

    async fetchSKUsForItems(items) {
      const skuMap = new Map();
      const productHandles = new Set();
      const itemsByHandle = new Map();
      
      items.forEach(item => {
        if (item.productHandle) {
          productHandles.add(item.productHandle);
          if (!itemsByHandle.has(item.productHandle)) {
            itemsByHandle.set(item.productHandle, []);
          }
          itemsByHandle.get(item.productHandle).push(item);
        }
      });
      
      if (productHandles.size === 0) return skuMap;
      
      // Fetch product JSON for each unique handle
      const skuPromises = Array.from(productHandles).map(async (handle) => {
        try {
          const productUrl = `/products/${handle}.json`;
          const response = await fetch(productUrl, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include'
          });
          
          if (!response.ok) {
            console.warn(`[Wishlist] Failed to fetch product ${handle}:`, response.status);
            return null;
          }
          
          const productData = await response.json();
          if (!productData.product || !productData.product.variants) {
            return null;
          }
          
          // Map variant IDs to SKUs
          productData.product.variants.forEach(variant => {
            const variantId = `gid://shopify/ProductVariant/${variant.id}`;
            if (variant.sku) {
              skuMap.set(variantId, variant.sku);
            }
          });
          
          return productData;
        } catch (error) {
          console.error(`[Wishlist] Error fetching product ${handle}:`, error);
          return null;
        }
      });
      
      await Promise.all(skuPromises);
      return skuMap;
    }

    async exportAllWishlistsToCSV() {
      if (!this.wishlists || this.wishlists.length === 0) {
        showNotification('No wishlists to export', 'error');
        return;
      }

      // Check if JSZip is available
      if (typeof JSZip === 'undefined') {
        showNotification('Zip library not loaded. Please refresh the page.', 'error');
        return;
      }

      try {
        const exportBtn = document.querySelector('.js-export-all-wishlists');
        const originalText = exportBtn ? exportBtn.textContent : 'Export All to CSV';
        if (exportBtn) {
          exportBtn.disabled = true;
          exportBtn.textContent = 'Exporting...';
        }

        // Create a new JSZip instance
        const zip = new JSZip();
        let exportedCount = 0;
        
        // Process each wishlist
        for (const wishlist of this.wishlists) {
          try {
            // Fetch full wishlist details to get items
            const url = getApiUrl(`/${wishlist.id}`, { customer_id: this.customerId });
            const response = await fetch(url, {
              method: 'GET',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include'
            });
            
            if (!response.ok) {
              console.warn(`[Wishlist] Failed to fetch wishlist ${wishlist.id}:`, response.status);
              continue;
            }
            
            const wishlistData = await response.json();
            if (!wishlistData.items || wishlistData.items.length === 0) {
              continue; // Skip empty wishlists
            }

            // Fetch SKUs for items in this wishlist
            const skuMap = await this.fetchSKUsForItems(wishlistData.items);
            
            // Build CSV rows for this wishlist
            const rows = ['sku,description,quantity'];
            
            wishlistData.items.forEach(item => {
              const sku = skuMap.get(item.variantId) || '';
              const description = item.variantTitle && item.variantTitle !== 'Default Title' && item.variantTitle !== item.productTitle
                ? `${item.productTitle} - ${item.variantTitle}`
                : item.productTitle || '';
              const quantity = item.quantity || 1;
              
              rows.push(`${this.escapeCSVField(sku)},${this.escapeCSVField(description)},${this.escapeCSVField(quantity)}`);
            });
            
            const csvContent = rows.join('\n');
            
            // Generate a safe filename from wishlist name
            const wishlistName = wishlistData.name || 'Unnamed Wishlist';
            const safeFilename = wishlistName
              .replace(/[^a-z0-9]/gi, '_')
              .replace(/_+/g, '_')
              .replace(/^_|_$/g, '') || 'wishlist';
            
            // Add CSV file to zip
            zip.file(`${safeFilename}.csv`, csvContent);
            exportedCount++;
          } catch (error) {
            console.error(`[Wishlist] Error processing wishlist ${wishlist.id}:`, error);
            continue;
          }
        }
        
        if (exportedCount === 0) {
          showNotification('No items found in any wishlist', 'error');
          if (exportBtn) {
            exportBtn.disabled = false;
            exportBtn.textContent = originalText;
          }
          return;
        }

        // Generate zip file
        const zipBlob = await zip.generateAsync({ type: 'blob' });
        
        // Download zip file
        const link = document.createElement('a');
        const url = URL.createObjectURL(zipBlob);
        const filename = `all-wishlists-${new Date().toISOString().split('T')[0]}.zip`;
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        showNotification(`Exported ${exportedCount} wishlist(s) as ZIP file!`);
        
        if (exportBtn) {
          exportBtn.disabled = false;
          exportBtn.textContent = originalText;
        }
      } catch (error) {
        console.error('[WishlistList] Error exporting all wishlists:', error);
        showNotification('Error exporting wishlists', 'error');
        
        const exportBtn = document.querySelector('.js-export-all-wishlists');
        if (exportBtn) {
          exportBtn.disabled = false;
          exportBtn.textContent = originalText;
        }
      }
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    formatDate(dateValue) {
      // Handle null, undefined, empty string, or empty object
      if (!dateValue || (typeof dateValue === 'object' && Object.keys(dateValue).length === 0)) {
        return '';
      }
      
      // If it's already a Date object, use it directly
      if (dateValue instanceof Date) {
        if (isNaN(dateValue.getTime())) {
          return '';
        }
        return dateValue.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
      
      // If it's an object with date properties, try to construct a date string
      if (typeof dateValue === 'object' && dateValue !== null) {
        // Try ISO string if available
        if (dateValue.toISOString && typeof dateValue.toISOString === 'function') {
          try {
            const date = new Date(dateValue.toISOString());
            if (!isNaN(date.getTime())) {
              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            }
          } catch (e) {
            // Fall through to string conversion
          }
        }
        // If object doesn't have useful date methods, return empty
        return '';
      }
      
      // Convert to string and try parsing
      try {
        const dateString = String(dateValue);
        if (!dateString || dateString.trim() === '') {
          return '';
        }
        const date = new Date(dateString);
        // Check if date is valid
        if (isNaN(date.getTime())) {
          return '';
        }
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch (error) {
        return '';
      }
    }

    downloadCSVTemplate() {
      const csvContent = 'sku,description,quantity\nSKU-001,Product Description 1,2\nSKU-002,Product Description 2,1';
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'wishlist-template.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    async handleCSVImport(form) {
      const formData = new FormData(form);
      const csvFile = formData.get('csvFile');
      const wishlistName = formData.get('name')?.trim();

      if (!csvFile || !wishlistName) {
        showNotification('Please provide a CSV file and wishlist name', 'error');
        return;
      }

      const submitBtn = form.querySelector('button[type="submit"]');
      const originalText = submitBtn ? submitBtn.textContent : 'Import';
      
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Importing...';
      }

      try {
        const importFormData = new FormData();
        importFormData.append('csvFile', csvFile);
        importFormData.append('customerId', this.customerId);
        importFormData.append('wishlistName', wishlistName);
        importFormData.append('shop', this.shopDomain);
        importFormData.append('normalizeToCurrentMoq', 'true');

        const url = `${API_URL}/import?shop=${encodeURIComponent(this.shopDomain)}&customer_id=${encodeURIComponent(this.customerId)}`;
        const response = await fetch(url, {
          method: 'POST',
          body: importFormData,
          credentials: 'include'
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to import CSV');
        }

        const result = await response.json();
        const modal = document.getElementById('wishlist-csv-import-modal');
        if (modal) modal.classList.add('hidden');
        form.reset();

        // Show results
        const successCount = result.results?.success || 0;
        const failedCount = result.results?.failed || 0;
        const adjustedCount = result.results?.adjusted || 0;
        if (failedCount > 0) {
          showNotification(`Import completed: ${successCount} items added, ${failedCount} failed, ${adjustedCount} MOQ-adjusted. Check console for details.`, 'warning');
          console.warn('[CSV Import] Errors:', result.results.errors);
        } else {
          const adjustmentSuffix = adjustedCount > 0 ? ` (${adjustedCount} quantities adjusted to current MOQ)` : '';
          showNotification(`Successfully imported ${successCount} items!${adjustmentSuffix}`);
        }

        // Reload wishlists
        await this.loadWishlists();

        // Redirect to the imported wishlist if it was newly created
        if (result.isNewWishlist && result.wishlist) {
          const redirectId = result.wishlist.humanReadableId || result.wishlist.id;
          setTimeout(() => {
            window.location.href = `/pages/wishlist?id=${redirectId}`;
          }, 1000);
        }
      } catch (error) {
        console.error('[Wishlist] Error importing CSV:', error);
        showNotification(error.message || 'Error importing CSV', 'error');
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalText;
        }
      }
    }
  }

  // Wishlist Detail Management
  class WishlistDetail {
    constructor(container) {
      this.container = container;
      this.customerId = container.dataset.customerId || CUSTOMER_ID;
      this.shopDomain = container.dataset.shopDomain || SHOP_DOMAIN;
      this.apiUrl = container.dataset.apiUrl || API_URL;
      
      // Get wishlist ID from data attribute or URL parameter
      const dataWishlistId = container.dataset.wishlistId;
      const urlWishlistId = this.getWishlistIdFromUrl();
      this.wishlistId = (dataWishlistId && dataWishlistId.trim() !== '') ? dataWishlistId : urlWishlistId;
      this.wishlist = null;
      this.selectedItemIds = new Set();
      this.skuByVariantId = new Map();
      this.productFetchCache = new Map();
      this.productFetchInFlight = new Map();
      this.variantHydrationCache = new Map();
      this.variantHydrationInFlight = new Map();
      this.addAllLabel = 'Add All to Cart';
      this.addSelectedLabel = 'Add Selected Items to Cart';
      // Out-of-stock threshold is a multiple of MOQ, matching the PDP and product cards.
      this.outOfStockThresholdMultiplier = container.dataset.thresholdOutOfStock;

      console.log('[WishlistDetail] Initializing...', {
        dataWishlistId: dataWishlistId,
        urlWishlistId: urlWishlistId,
        finalWishlistId: this.wishlistId,
        currentUrl: window.location.href,
        search: window.location.search,
        pathname: window.location.pathname,
        hash: window.location.hash
      });

      // Always initialize - errors will be shown after timeout if loading fails
      this.init();
    }

    getWishlistIdFromUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      let id = urlParams.get('id');
      
      // Also try getting from hash fragment (in case URL uses #id= format)
      if (!id && window.location.hash) {
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        id = hashParams.get('id');
      }
      
      // Try to extract from pathname if it's in the URL path (e.g., /pages/wishlist/{id})
      if (!id) {
        const pathParts = window.location.pathname.split('/').filter(p => p);
        const wishlistIndex = pathParts.indexOf('wishlist');
        if (wishlistIndex !== -1 && pathParts.length > wishlistIndex + 1) {
          id = pathParts[wishlistIndex + 1];
        }
      }
      
      console.log('[WishlistDetail] Extracting ID from URL:', {
        search: window.location.search,
        hash: window.location.hash,
        pathname: window.location.pathname,
        id: id
      });
      
      return id ? id.trim() : null;
    }

    async init() {
      await this.loadWishlist();
      this.setupEventListeners();
    }

    async loadWishlist() {
      const loadingEl = this.container.querySelector('.wishlist-detail__loading');
      const contentEl = this.container.querySelector('.wishlist-detail__content');
      const emptyEl = this.container.querySelector('.wishlist-detail__empty');

      if (!loadingEl) {
        console.error('[WishlistDetail] Loading element not found');
        return;
      }

      // Set up error timeout (5 seconds)
      let hasCompleted = false;
      let timeoutFired = false;
      const errorTimeout = setTimeout(() => {
        if (!hasCompleted) {
          timeoutFired = true;
          
          // Check for missing customer ID
          if (!this.customerId || this.customerId === '' || this.customerId === 'null' || this.customerId === 'undefined') {
            console.warn('[WishlistDetail] Customer ID not found or invalid:', this.customerId);
            loadingEl.innerHTML = '<p style="color: #BF0711;">Please log in to view wishlists.</p>';
            return;
          }

          // Check for missing wishlist ID
          if (!this.wishlistId || this.wishlistId.trim() === '') {
            console.error('[WishlistDetail] ❌ Wishlist ID not found!');
            loadingEl.innerHTML = `
              <div style="padding: 2rem; text-align: center; background: #F6F6F7; border-radius: 8px; border: 1px solid #E1E3E5;">
                <h1 class="h2" style="margin-bottom: 1rem;">Wishlist</h1>
                <p style="font-size: 1rem; color: #6D7175; margin-bottom: 1rem;">
                  No wishlist ID provided. Please visit this page from a wishlist link.
                </p>
                <a href="/pages/my-wishlist" class="btn btn--primary">
                  Back to Wishlists
                </a>
              </div>
            `;
            if (contentEl) {
              contentEl.classList.add('hidden');
            }
            return;
          }

          // Generic loading timeout error - only show if still loading
          if (!hasCompleted) {
            loadingEl.innerHTML = '<p style="color: #BF0711;">Loading is taking longer than expected. Please refresh the page or check your connection.</p>';
          }
        }
      }, 5000);

      console.log('[WishlistDetail] Loading wishlist:', this.wishlistId);

      try {
        // Validate IDs before attempting to load
        if (!this.customerId || this.customerId === '' || this.customerId === 'null' || this.customerId === 'undefined') {
          // Wait for timeout to show error
          return;
        }

        if (!this.wishlistId || this.wishlistId.trim() === '') {
          // Wait for timeout to show error
          return;
        }

        const url = getApiUrl(`/${this.wishlistId}`, { customer_id: this.customerId });
        console.log('[WishlistDetail] Fetching from URL:', url);
        
        const response = await fetch(url, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include'
        });
        console.log('[WishlistDetail] Response status:', response.status);

        if (!response.ok) {
          const errorText = await response.text();
          console.error('[WishlistDetail] API error response:', errorText);
          throw new Error(`Failed to load wishlist: ${response.status} ${response.statusText}`);
        }

        const wishlist = await response.json();
        console.log('[WishlistDetail] API response data:', wishlist);
        
        this.wishlist = wishlist;
        hasCompleted = true;
        clearTimeout(errorTimeout);

        // Always show content when we successfully load
        if (loadingEl) loadingEl.style.display = 'none';
        if (contentEl) {
          contentEl.classList.remove('hidden');
          this.renderWishlist();
        }
      } catch (error) {
        hasCompleted = true;
        clearTimeout(errorTimeout);
        console.error('[WishlistDetail] Error loading wishlist:', error);
        if (loadingEl) {
          loadingEl.innerHTML = `<p style="color: #BF0711;">Error loading wishlist: ${error.message}. Please refresh the page or check the console for details.</p>`;
        }
      }
    }

    renderWishlist() {
      if (!this.wishlist) return;

      // Update title
      const titleEl = this.container.querySelector('.wishlist-detail__title-text');
      if (titleEl) {
        titleEl.textContent = this.wishlist.name || 'Untitled Wishlist';
      }

      // Update description
      const descEl = this.container.querySelector('.wishlist-detail__description-text');
      if (descEl) {
        descEl.textContent = this.wishlist.description || '';
      }

      // Update meta
      const itemCountEl = this.container.querySelector('.wishlist-detail__item-count');
      if (itemCountEl) {
        const count = this.wishlist.items?.length || 0;
        itemCountEl.textContent = `${count} ${count === 1 ? 'item' : 'items'}`;
      }

      const updatedEl = this.container.querySelector('.wishlist-detail__updated-date');
      if (updatedEl && this.wishlist.updatedAt) {
        updatedEl.textContent = `Updated ${this.formatDate(this.wishlist.updatedAt)}`;
      }

      // Render items
      this.renderItems();
    }

    getCurrentItemIds() {
      if (!this.wishlist || !Array.isArray(this.wishlist.items)) return [];
      return this.wishlist.items.map((item) => item.id);
    }

    reconcileSelectionState() {
      const validIds = new Set(this.getCurrentItemIds());
      this.selectedItemIds.forEach((itemId) => {
        if (!validIds.has(itemId)) {
          this.selectedItemIds.delete(itemId);
        }
      });
    }

    getItemSku(item) {
      if (!item) return '';
      if (item.metadata && typeof item.metadata === 'object' && item.metadata.sku) {
        return String(item.metadata.sku);
      }
      if (item.metadata && typeof item.metadata === 'string') {
        try {
          const parsedMetadata = JSON.parse(item.metadata);
          if (parsedMetadata && parsedMetadata.sku) {
            return String(parsedMetadata.sku);
          }
        } catch (e) {
          // ignore malformed metadata
        }
      }
      return this.skuByVariantId.get(item.variantId) || '';
    }

    getStockTypeProperties(item) {
      if (!item) return null;

      let metadata = item.metadata;
      if (metadata && typeof metadata === 'string') {
        try {
          metadata = JSON.parse(metadata);
        } catch (e) {
          metadata = null;
        }
      }

      if (!metadata || typeof metadata !== 'object') {
        return null;
      }

      const stockType = String(metadata._stock_type || '').trim();
      if (!stockType) {
        return null;
      }

      return {
        _stock_type: stockType,
        _dispatch_window_start: String(metadata._dispatch_window_start || ''),
        _dispatch_window_end: String(metadata._dispatch_window_end || ''),
        _forward_delivery_date: String(metadata._forward_delivery_date || '')
      };
    }

    updateSelectionUi() {
      const selectAllInput = this.container.querySelector('.js-wishlist-select-all');
      const selectAllLabel = this.container.querySelector('.js-wishlist-select-all-label');
      const selectAllWrap = this.container.querySelector('.wishlist-detail__select-all');
      const totalItems = this.getCurrentItemIds().length;
      const selectedCount = this.selectedItemIds.size;

      if (selectAllWrap) {
        if (totalItems > 0) {
          selectAllWrap.classList.remove('hidden');
        } else {
          selectAllWrap.classList.add('hidden');
        }
      }

      if (selectAllInput) {
        selectAllInput.checked = totalItems > 0 && selectedCount === totalItems;
        selectAllInput.indeterminate = selectedCount > 0 && selectedCount < totalItems;
      }

      if (selectAllLabel) {
        selectAllLabel.textContent = selectedCount === totalItems && totalItems > 0 ? 'Unselect all' : 'Select all';
      }

      const addAllBtn = this.container.querySelector('.js-add-all-to-cart');
      if (addAllBtn) {
        const selectedMode = selectedCount > 0;
        const nextText = selectedMode ? this.addSelectedLabel : this.addAllLabel;
        if (!addAllBtn.classList.contains('is-loading')) {
          addAllBtn.textContent = nextText;
        }
      }
    }

    renderItems() {
      const itemsListEl = this.container.querySelector('.wishlist-detail__items-list');
      const emptyEl = this.container.querySelector('.wishlist-detail__empty');

      this.reconcileSelectionState();

      if (!this.wishlist.items || this.wishlist.items.length === 0) {
        if (emptyEl) emptyEl.classList.remove('hidden');
        if (itemsListEl) itemsListEl.style.display = 'none';
        this.updateSelectionUi();
        return;
      }

      if (emptyEl) emptyEl.classList.add('hidden');
      if (itemsListEl) {
        itemsListEl.style.display = 'block';
        itemsListEl.innerHTML = this.wishlist.items.map(item => {
          // Build product URL from handle
          const productUrl = item.productHandle ? `/products/${this.escapeHtml(item.productHandle)}` : '#';
          
          // Format price - price is stored as Float in database (in dollars)
          let displayPrice = '';
          let unitPrice = 0;
          if (item.price != null && item.price !== undefined && item.price !== '') {
            const priceNum = parseFloat(item.price);
            if (!isNaN(priceNum) && priceNum > 0) {
              // Price is already in dollars, format with 2 decimal places
              displayPrice = `$${priceNum.toFixed(2)}`;
              unitPrice = priceNum;
            }
          }
          
          // Quantity default to 1
          const quantity = item.quantity || 1;
          
          // Calculate line total (price × quantity)
          const unitPriceCents = Math.round(unitPrice * 100);
          const lineTotal = unitPrice * quantity;
          const lineTotalDisplay = unitPrice > 0 ? `$${lineTotal.toFixed(2)}` : '';
          
          // Get MOQ from metadata, default to 1 if not present
          let moq = 1;
          if (item.metadata && typeof item.metadata === 'object') {
            moq = item.metadata.moq || item.metadata.minimum_order_quantity || 1;
          } else if (item.metadata && typeof item.metadata === 'string') {
            try {
              const parsedMetadata = JSON.parse(item.metadata);
              moq = parsedMetadata.moq || parsedMetadata.minimum_order_quantity || 1;
            } catch (e) {
              // If parsing fails, default to 1
              moq = 1;
            }
          }
          moq = getEffectiveMoq(parseInt(moq, 10) || 1);
          
          // Debug logging to verify MOQ is being read correctly
          const metadataMoq = item.metadata?.moq;
          const metadataMinOrderQty = item.metadata?.minimum_order_quantity;
          console.log('[WishlistDetail] Item MOQ:', {
            itemId: item.id,
            variantId: item.variantId,
            metadata: JSON.stringify(item.metadata), // Stringify to see full contents
            metadataKeys: item.metadata ? Object.keys(item.metadata) : [],
            moq: moq,
            metadataType: typeof item.metadata,
            metadataMoq: metadataMoq,
            metadataMinOrderQty: metadataMinOrderQty,
            metadataMoqType: typeof metadataMoq,
            metadataMinOrderQtyType: typeof metadataMinOrderQty
          });
          
          // Ensure quantity is at least MOQ and a multiple of MOQ
          const adjustedQuantity = normalizeQtyWithMoq(quantity, moq).quantity;
          const isChecked = this.selectedItemIds.has(item.id);
          const sku = this.getItemSku(item);
          
          return `
            <div class="wishlist-item-row" data-item-id="${item.id}" data-moq="${moq}" data-unit-price-cents="${unitPriceCents}" data-qty="${adjustedQuantity}">
              <div class="wishlist-item__select">
                <input
                  type="checkbox"
                  class="js-wishlist-item-select"
                  data-item-id="${item.id}"
                  ${isChecked ? 'checked' : ''}
                  aria-label="Select ${this.escapeHtml(item.productTitle || 'item')}"
                />
              </div>
              <div class="wishlist-item__image">
                <a href="${productUrl}" class="block">
                  ${item.imageUrl 
                    ? `<img src="${this.escapeHtml(item.imageUrl)}" alt="${this.escapeHtml(item.productTitle)}" loading="lazy" />`
                    : '<div class="media relative" style="padding-top: 100%;"><div class="media__placeholder img-fit">No image</div></div>'
                  }
                </a>
              </div>
              <div class="wishlist-item__details">
                <a class="wishlist-item__title" href="${productUrl}">
                  ${this.escapeHtml(item.productTitle)}
                </a>
                ${item.variantTitle && item.variantTitle !== 'Default Title' && item.variantTitle !== item.productTitle
                  ? `<div class="wishlist-item__variant">${this.escapeHtml(item.variantTitle)}</div>`
                  : ''
                }
                <div class="wishlist-item__sku" data-item-id="${item.id}">SKU: ${sku ? this.escapeHtml(sku) : '--'}</div>
                <div class="wishlist-item__soh-badge" data-item-id="${item.id}"></div>
                <div class="wishlist-item__price" data-item-id="${item.id}">
                  ${displayPrice 
                    ? `<div class="flex flex-col items-end gap-1"><span class="text-sm text-theme-light">${displayPrice} each</span><strong class="price__current wishlist-item__line-total">${lineTotalDisplay}</strong></div>`
                    : `<span class="wishlist-item__price-loading text-sm text-theme-light">Loading price...</span>`
                  }
                </div>
              </div>
              <div class="wishlist-item__quantity">
                <quantity-input class="cart-item__qty">
                  <div class="qty-input qty-input--joined inline-flex items-center">
                    <button type="button" class="qty-input__btn btn btn--minus" name="minus" aria-controls="wishlist-qty-${item.id}" data-item-id="${item.id}">
                      <span class="visually-hidden">Decrease quantity</span>
                    </button>
                    <input 
                      class="qty-input__input input" 
                      type="number" 
                      id="wishlist-qty-${item.id}"
                      name="quantity" 
                      value="${adjustedQuantity}" 
                      min="${moq}" 
                      step="${moq}"
                      data-item-id="${item.id}"
                      data-moq="${moq}"
                      data-initial-value="${adjustedQuantity}"
                      aria-label="Quantity"
                    />
                    <button type="button" class="qty-input__btn btn btn--plus" name="plus" aria-controls="wishlist-qty-${item.id}" data-item-id="${item.id}">
                      <span class="visually-hidden">Increase quantity</span>
                    </button>
                  </div>
                </quantity-input>
              </div>
              <div class="wishlist-item__actions">
                <button type="button" class="btn btn--secondary btn--sm js-remove-item" data-item-id="${item.id}">
                  Remove
                </button>
              </div>
            </div>
          `;
        }).join('');
        
        // Setup item interactions after rendering
        this.setupItemInteractions();
        this.updateSelectionUi();
        
        // Fetch and update prices/SKUs using a shared batched fetch pipeline.
        this.fetchItemPrices();
        
        // Update totals after rendering
        this.updateTotals();
      }
    }
    
    async populateItemSkus() {
      if (!this.wishlist || !Array.isArray(this.wishlist.items) || this.wishlist.items.length === 0) return;

      try {
        const skuMap = await this.fetchSKUsForItems(this.wishlist.items);
        if (!(skuMap instanceof Map) || skuMap.size === 0) return;

        this.skuByVariantId = skuMap;
        this.wishlist.items.forEach((item) => {
          const sku = this.getItemSku(item);
          if (!sku) return;
          const skuEl = this.container.querySelector(`.wishlist-item__sku[data-item-id="${item.id}"]`);
          if (skuEl) {
            skuEl.textContent = `SKU: ${sku}`;
          }
        });
      } catch (error) {
        console.warn('[WishlistDetail] Failed to populate item SKUs:', error);
      }
    }

    async fetchProductByHandle(handle) {
      if (!handle) return { handle, product: null };
      if (this.productFetchCache.has(handle)) {
        return { handle, product: this.productFetchCache.get(handle) };
      }
      if (this.productFetchInFlight.has(handle)) {
        return this.productFetchInFlight.get(handle);
      }

      const requestPromise = (async () => {
        try {
          const response = await fetch(`/products/${handle}.json`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include'
          });

          if (!response.ok) {
            console.warn('[WishlistDetail] Failed to fetch product JSON for', handle, 'Status:', response.status);
            this.productFetchCache.set(handle, null);
            return { handle, product: null };
          }

          const productData = await response.json();
          const product = productData?.product || null;
          this.productFetchCache.set(handle, product);
          return { handle, product };
        } catch (error) {
          console.error('[WishlistDetail] Error fetching product JSON for', handle, error);
          this.productFetchCache.set(handle, null);
          return { handle, product: null };
        } finally {
          this.productFetchInFlight.delete(handle);
        }
      })();

      this.productFetchInFlight.set(handle, requestPromise);
      return requestPromise;
    }

    async fetchProductsByHandles(handles, options = {}) {
      const concurrency = Math.max(1, parseInt(options.concurrency, 10) || 4);
      const uniqueHandles = Array.from(new Set((handles || []).filter(Boolean)));
      const results = [];

      for (let i = 0; i < uniqueHandles.length; i += concurrency) {
        const batch = uniqueHandles.slice(i, i + concurrency);
        const batchResults = await Promise.all(batch.map((handle) => this.fetchProductByHandle(handle)));
        results.push(...batchResults);
      }

      return results;
    }

    normalizeHydratedPrice(rawPrice, fallbackPrice) {
      const parsedRaw = rawPrice != null ? parseFloat(String(rawPrice)) : NaN;
      if (!Number.isFinite(parsedRaw) || parsedRaw <= 0) {
        return Number.isFinite(parseFloat(String(fallbackPrice))) ? parseFloat(String(fallbackPrice)) : null;
      }

      const hasDecimal = String(rawPrice).includes('.');
      if (hasDecimal) return parsedRaw;

      const parsedFallback = fallbackPrice != null ? parseFloat(String(fallbackPrice)) : NaN;
      if (Number.isFinite(parsedFallback) && parsedFallback > 0) {
        const ratio = parsedRaw / parsedFallback;
        if (ratio > 50 && ratio < 150) {
          return parsedRaw / 100;
        }
      }

      if (parsedRaw > 10000 && parsedRaw === Math.floor(parsedRaw)) {
        return parsedRaw / 100;
      }

      return parsedRaw;
    }

    applyHydrationToItem(item, hydration) {
      if (!item || !hydration) return;

      const numericVariantId = hydration.numericVariantId || (String(item.variantId || '').includes('/')
        ? parseInt(String(item.variantId).split('/').pop(), 10)
        : parseInt(String(item.variantId || ''), 10));
      if (numericVariantId && Number.isFinite(numericVariantId)) {
        this.variantHydrationCache.set(numericVariantId, hydration);
      }

      if (hydration.sku) {
        this.skuByVariantId.set(item.variantId, hydration.sku);
        const skuEl = this.container.querySelector(`.wishlist-item__sku[data-item-id="${item.id}"]`);
        if (skuEl) skuEl.textContent = `SKU: ${hydration.sku}`;
      }

      const metadataObj = (item.metadata && typeof item.metadata === 'object') ? item.metadata : {};
      if (hydration.stockType && hydration.stockType._stock_type) {
        metadataObj._stock_type = hydration.stockType._stock_type;
        metadataObj._dispatch_window_start = hydration.stockType._dispatch_window_start || '';
        metadataObj._dispatch_window_end = hydration.stockType._dispatch_window_end || '';
        metadataObj._forward_delivery_date = hydration.stockType._forward_delivery_date || '';
      }

      const moq = getEffectiveMoq(parseInt(hydration.moq, 10));

      const badgeEl = this.container.querySelector(`.wishlist-item__soh-badge[data-item-id="${item.id}"]`);
      if (badgeEl) {
        const badge = resolveWishlistStockBadge({
          inventoryQuantity: hydration.inventoryQuantity,
          eta: hydration.eta,
          // Product MOQ, not the company-override MOQ: the PDP and product cards read the
          // product metafield, and the badge has to agree with them.
          moq: parseInt(hydration.moq, 10),
          stockType: metadataObj._stock_type,
          thresholdMultiplier: this.outOfStockThresholdMultiplier
        });
        if (badge) {
          badgeEl.className = `wishlist-item__soh-badge soh-badge ${badge.cls}`;
          badgeEl.textContent = badge.label;
        } else {
          badgeEl.className = 'wishlist-item__soh-badge';
          badgeEl.textContent = '';
        }
      }

      if (Number.isFinite(moq) && moq > 0) {
        metadataObj.moq = moq;
        metadataObj.minimum_order_quantity = moq;
        item.metadata = metadataObj;

        const row = this.container.querySelector(`.wishlist-item-row[data-item-id="${item.id}"]`);
        const input = this.container.querySelector(`input[name="quantity"][data-item-id="${item.id}"]`);
        if (row) row.dataset.moq = String(moq);
        if (input) {
          input.setAttribute('min', String(moq));
          input.setAttribute('step', String(moq));
          input.dataset.moq = String(moq);
          const qty = normalizeQtyWithMoq(parseInt(input.value, 10) || moq, moq).quantity;
          input.value = String(qty);
          input.dataset.initialValue = String(qty);
          if (row) row.dataset.qty = String(qty);
        }
      }
      item.metadata = metadataObj;

      const priceInDollars = this.normalizeHydratedPrice(hydration.price, item.price);
      if (!Number.isFinite(priceInDollars) || priceInDollars <= 0) return;
      item.price = priceInDollars;

      const priceEl = this.container.querySelector(`.wishlist-item__price[data-item-id="${item.id}"]`);
      const row = this.container.querySelector(`.wishlist-item-row[data-item-id="${item.id}"]`);
      if (priceEl && row) {
        const quantity = parseInt(row.dataset.qty || '1', 10);
        const unitPriceCents = Math.round(priceInDollars * 100);
        const lineTotal = priceInDollars * quantity;
        row.dataset.unitPriceCents = unitPriceCents.toString();
        priceEl.innerHTML = `<div class="flex flex-col items-end gap-1"><span class="text-sm text-theme-light">$${priceInDollars.toFixed(2)} each</span><strong class="price__current wishlist-item__line-total">$${lineTotal.toFixed(2)}</strong></div>`;
      }
    }

    async hydrateItemsFromBackend(items) {
      const targetItems = (items || []).filter((item) => item && item.id && item.variantId);
      if (!targetItems.length) return;

      const numericVariantIds = targetItems.map((item) => {
        const raw = String(item.variantId || '');
        const variantPart = raw.includes('/') ? raw.split('/').pop() : raw;
        const parsed = parseInt(String(variantPart || '').trim(), 10);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
      });

      const allCached = numericVariantIds.every((id) => id && this.variantHydrationCache.has(id));
      if (allCached) {
        targetItems.forEach((item, idx) => {
          const numericId = numericVariantIds[idx];
          if (!numericId) return;
          this.applyHydrationToItem(item, this.variantHydrationCache.get(numericId));
        });
        this.updateTotals();
        return;
      }

      const requestKey = targetItems.map((item) => item.id).sort().join('|');
      if (this.variantHydrationInFlight.has(requestKey)) {
        await this.variantHydrationInFlight.get(requestKey);
        targetItems.forEach((item, idx) => {
          const numericId = numericVariantIds[idx];
          if (!numericId) return;
          this.applyHydrationToItem(item, this.variantHydrationCache.get(numericId));
        });
        this.updateTotals();
        return;
      }

      const currency = window.Shopify?.currency?.active || window.Shopify?.currency?.default || null;
      const requestPromise = (async () => {
        const url = getApiUrl(`/${this.wishlistId}`, { action: 'hydrate-items', shop: this.shopDomain });
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            customerId: this.customerId,
            itemIds: targetItems.map((item) => item.id),
            currency
          })
        });

        if (!response.ok) {
          throw new Error(`Wishlist hydration failed (${response.status})`);
        }
        const data = await response.json();
        const hydratedItems = Array.isArray(data?.items) ? data.items : [];
        hydratedItems.forEach((entry) => {
          if (!entry?.numericVariantId) return;
          this.variantHydrationCache.set(entry.numericVariantId, entry);
        });
      })().finally(() => {
        this.variantHydrationInFlight.delete(requestKey);
      });

      this.variantHydrationInFlight.set(requestKey, requestPromise);
      await requestPromise;

      targetItems.forEach((item, idx) => {
        const numericId = numericVariantIds[idx];
        if (!numericId) return;
        this.applyHydrationToItem(item, this.variantHydrationCache.get(numericId));
      });
      this.updateTotals();
    }

    async fetchItemPrices() {
      if (!this.wishlist.items || this.wishlist.items.length === 0) return;

      const items = this.wishlist.items.filter((item) => item && item.id && item.variantId);
      if (!items.length) return;

      const chunkSize = 25;
      for (let i = 0; i < items.length; i += chunkSize) {
        const chunk = items.slice(i, i + chunkSize);
        try {
          await this.hydrateItemsFromBackend(chunk);
        } catch (error) {
          console.warn('[WishlistDetail] Backend hydration failed for chunk, falling back to product JSON', error);
          // Fallback to existing storefront-handle based fetch for resilience.
          const fallbackHandles = Array.from(new Set(chunk.map((item) => item.productHandle).filter(Boolean)));
          const itemsByHandle = new Map();
          chunk.forEach((item) => {
            if (!item.productHandle) return;
            if (!itemsByHandle.has(item.productHandle)) itemsByHandle.set(item.productHandle, []);
            itemsByHandle.get(item.productHandle).push(item);
          });
          const results = await this.fetchProductsByHandles(fallbackHandles, { concurrency: 2 });
          results.forEach(({ handle, product }) => {
            if (!product || !product.variants) return;
            const handleItems = itemsByHandle.get(handle) || [];
            handleItems.forEach((item) => {
              const variantIdToMatch = String(item.variantId || '').includes('/')
                ? String(item.variantId || '').split('/').pop()
                : String(item.variantId || '');
              const variant = product.variants.find((v) => {
                const vIdStr = String(v.id);
                const vIdNumeric = vIdStr.includes('/') ? vIdStr.split('/').pop() : vIdStr;
                return vIdNumeric === variantIdToMatch || vIdStr === item.variantId || String(v.id) === variantIdToMatch;
              });
              if (!variant) return;
              this.applyHydrationToItem(item, {
                numericVariantId: parseInt(variantIdToMatch, 10),
                sku: variant.sku || '',
                moq: item?.metadata?.moq || item?.metadata?.minimum_order_quantity || 1,
                price: variant.price
              });
            });
          });
          this.updateTotals();
        }
      }
    }
    
    setupItemInteractions() {
      // Quantity update handlers - listen for change events on inputs (triggered by quantity-input component)
      this.container.addEventListener('change', (e) => {
        if (e.target.matches('.js-wishlist-item-select')) {
          const itemId = e.target.dataset.itemId;
          if (itemId) {
            if (e.target.checked) {
              this.selectedItemIds.add(itemId);
            } else {
              this.selectedItemIds.delete(itemId);
            }
            this.updateSelectionUi();
          }
          return;
        }

        if (e.target.matches('.wishlist-item__quantity input[name="quantity"]')) {
          const itemId = e.target.dataset.itemId;
          const effectiveMoq = getEffectiveMoq(e.target.dataset.moq);
          const normalized = normalizeQtyWithMoq(e.target.value, effectiveMoq);
          let newQuantity = normalized.quantity;
          e.target.value = String(newQuantity);
          
          const initialValue = parseInt(e.target.dataset.initialValue) || effectiveMoq;
          if (newQuantity > 0 && newQuantity !== initialValue) {
            this.updateItemQuantity(itemId, newQuantity);
            // Update initial value after successful update
            e.target.dataset.initialValue = newQuantity.toString();
          } else if (newQuantity !== initialValue) {
            // Reset to previous value if invalid
            e.target.value = initialValue.toString();
          }
        }
      });
      
      // Also handle blur event to round manually entered values
      this.container.addEventListener('blur', (e) => {
        if (e.target.matches('.wishlist-item__quantity input[name="quantity"]')) {
          const effectiveMoq = getEffectiveMoq(e.target.dataset.moq);
          const normalized = normalizeQtyWithMoq(e.target.value, effectiveMoq);
          if (normalized.adjusted) {
            e.target.value = String(normalized.quantity);
            // Trigger change event to update the wishlist
            e.target.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }, true); // Use capture phase to catch blur events
      
      // Remove item handlers (use delegation for dynamic content)
      this.container.addEventListener('click', async (e) => {
        const removeBtn = e.target.closest('.js-remove-item');
        if (removeBtn) {
          const itemId = removeBtn.dataset.itemId;
          if (itemId) {
            const confirmed = await showConfirmModal(
              'Remove Item',
              'Are you sure you want to remove this item from your wishlist?'
            );
            if (confirmed) {
              await this.removeItem(itemId);
            }
          }
        }
      });
    }
    
    async updateItemQuantity(itemId, quantity) {
      if (!itemId || !quantity || quantity < 1) return;
      if (!this.customerId) {
        showNotification('Please log in to update quantity', 'error');
        return;
      }
      
      // Get MOQ from the row to validate quantity
      const row = this.container.querySelector(`.wishlist-item-row[data-item-id="${itemId}"]`);
      const moq = getEffectiveMoq(row ? row.dataset.moq : 1);
      const normalized = normalizeQtyWithMoq(quantity, moq);
      quantity = normalized.quantity;
      
      try {
        const url = getApiUrl(`/${this.wishlistId}`, { action: 'update-item' });
        const response = await fetch(url, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            customerId: this.customerId,
            itemId,
            quantity: parseInt(quantity)
          })
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('[WishlistDetail] Update quantity error:', errorText);
          throw new Error('Failed to update quantity');
        }
        
        // Update the input value to reflect the rounded quantity
        const input = this.container.querySelector(`input[name="quantity"][data-item-id="${itemId}"]`);
        if (input) {
          input.value = quantity;
          input.dataset.initialValue = quantity.toString();
        }
        
        // Update the data-qty attribute on the row
        if (row) {
          row.dataset.qty = quantity.toString();
          this.updateItemLineTotal(row);
          this.updateTotals();
        }
        
        // Reload wishlist to get updated data (optional - can skip for better UX)
        // await this.loadWishlist();
        showNotification('Quantity updated', 'success');
      } catch (error) {
        console.error('[WishlistDetail] Error updating quantity:', error);
        showNotification('Failed to update quantity', 'error');
        // Restore previous value on error
        const input = this.container.querySelector(`input[name="quantity"][data-item-id="${itemId}"]`);
        if (input) {
          input.value = input.dataset.initialValue || String(moq);
        }
      }
    }
    
    async removeItem(itemId) {
      if (!itemId) return;
      if (!this.customerId) {
        showNotification('Please log in to remove items', 'error');
        return;
      }
      
      try {
        const url = getApiUrl(`/${this.wishlistId}`, { action: 'remove-item' });
        const response = await fetch(url, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ 
            customerId: this.customerId,
            itemId 
          })
        });
        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('[WishlistDetail] Remove item error:', errorText);
          throw new Error('Failed to remove item');
        }

        this.selectedItemIds.delete(itemId);
        
        // Reload wishlist to refresh the list
        await this.loadWishlist();
        this.updateTotals();
        showNotification('Item removed from wishlist', 'success');
      } catch (error) {
        console.error('[WishlistDetail] Error removing item:', error);
        showNotification('Failed to remove item', 'error');
      }
    }

    downloadCSVTemplate() {
      const csvContent = 'sku,description,quantity\nSKU-001,Product Description 1,2\nSKU-002,Product Description 2,1';
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'wishlist-template.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }

    async fetchSKUsForItems(items) {
      // Group items by product handle to batch requests
      const productHandles = new Set();
      const itemsByHandle = new Map();
      
      items.forEach(item => {
        if (item.productHandle) {
          productHandles.add(item.productHandle);
          if (!itemsByHandle.has(item.productHandle)) {
            itemsByHandle.set(item.productHandle, []);
          }
          itemsByHandle.get(item.productHandle).push(item);
        }
      });
      
      if (productHandles.size === 0) return new Map();
      
      // Fetch product JSON for each unique handle
      const promises = Array.from(productHandles).map(async (handle) => {
        try {
          const productUrl = `/products/${handle}.json`;
          const response = await fetch(productUrl, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include'
          });
          
          if (!response.ok) {
            console.warn('[WishlistDetail] Failed to fetch product JSON for', handle);
            return { handle, product: null };
          }
          
          const productData = await response.json();
          return { handle, product: productData.product };
        } catch (error) {
          console.error('[WishlistDetail] Error fetching product JSON for', handle, error);
          return { handle, product: null };
        }
      });
      
      const results = await Promise.all(promises);
      
      // Map variant IDs to SKUs
      const skuMap = new Map();
      results.forEach(({ handle, product }) => {
        if (!product || !product.variants) return;
        
        const items = itemsByHandle.get(handle) || [];
        items.forEach(item => {
          // Extract numeric variant ID from GID if needed
          let variantIdToMatch = null;
          if (item.variantId) {
            if (item.variantId.includes('/')) {
              variantIdToMatch = item.variantId.split('/').pop();
            } else {
              variantIdToMatch = item.variantId;
            }
          }
          
          if (!variantIdToMatch) return;
          
          // Find matching variant
          const variant = product.variants.find(v => {
            const vIdStr = String(v.id);
            const vIdNumeric = vIdStr.includes('/') ? vIdStr.split('/').pop() : vIdStr;
            return vIdNumeric === variantIdToMatch || vIdStr === item.variantId || String(v.id) === variantIdToMatch;
          });
          
          if (variant && variant.sku) {
            skuMap.set(item.variantId, variant.sku);
          }
        });
      });
      
      return skuMap;
    }

    escapeCSVField(field) {
      if (field == null || field === undefined) return '';
      const str = String(field);
      // If field contains comma, quote, or newline, wrap in quotes and escape quotes
      if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    }

    async exportWishlistToCSV() {
      if (!this.wishlist || !this.wishlist.items || this.wishlist.items.length === 0) {
        showNotification('Wishlist is empty', 'error');
        return;
      }

      try {
        // Fetch SKUs for all items
        const skuMap = await this.fetchSKUsForItems(this.wishlist.items);
        
        // Build CSV rows
        const rows = ['sku,description,quantity'];
        
        this.wishlist.items.forEach(item => {
          const sku = skuMap.get(item.variantId) || '';
          const description = item.variantTitle && item.variantTitle !== 'Default Title' && item.variantTitle !== item.productTitle
            ? `${item.productTitle} - ${item.variantTitle}`
            : item.productTitle || '';
          const quantity = item.quantity || 1;
          
          rows.push(`${this.escapeCSVField(sku)},${this.escapeCSVField(description)},${this.escapeCSVField(quantity)}`);
        });
        
        const csvContent = rows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        const filename = `${this.escapeHtml(this.wishlist.name || 'wishlist').replace(/[^a-z0-9]/gi, '_')}.csv`;
        link.setAttribute('href', url);
        link.setAttribute('download', filename);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        showNotification('Wishlist exported successfully!');
      } catch (error) {
        console.error('[WishlistDetail] Error exporting wishlist:', error);
        showNotification('Error exporting wishlist', 'error');
      }
    }

    async handleCSVImportDetail(form) {
      const formData = new FormData(form);
      const csvFile = formData.get('csvFile');

      if (!csvFile) {
        showNotification('Please provide a CSV file', 'error');
        return;
      }

      const submitBtn = form.querySelector('button[type="submit"]');
      const originalText = submitBtn ? submitBtn.textContent : 'Import';
      
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Importing...';
      }

      try {
        const importFormData = new FormData();
        importFormData.append('csvFile', csvFile);
        importFormData.append('customerId', this.customerId);
        importFormData.append('wishlistId', this.wishlistId);
        importFormData.append('shop', this.shopDomain);
        importFormData.append('normalizeToCurrentMoq', 'true');

        const url = `${API_URL}/import?shop=${encodeURIComponent(this.shopDomain)}&customer_id=${encodeURIComponent(this.customerId)}`;
        const response = await fetch(url, {
          method: 'POST',
          body: importFormData,
          credentials: 'include'
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to import CSV');
        }

        const result = await response.json();
        const modal = document.getElementById('wishlist-csv-import-modal-detail');
        if (modal) modal.classList.add('hidden');
        form.reset();

        // Show results
        const successCount = result.results?.success || 0;
        const failedCount = result.results?.failed || 0;
        const adjustedCount = result.results?.adjusted || 0;
        if (failedCount > 0) {
          showNotification(`Import completed: ${successCount} items added, ${failedCount} failed, ${adjustedCount} MOQ-adjusted. Check console for details.`, 'warning');
          console.warn('[CSV Import] Errors:', result.results.errors);
        } else {
          const adjustmentSuffix = adjustedCount > 0 ? ` (${adjustedCount} quantities adjusted to current MOQ)` : '';
          showNotification(`Successfully imported ${successCount} items!${adjustmentSuffix}`);
        }

        // Reload wishlist
        await this.loadWishlist();
      } catch (error) {
        console.error('[Wishlist] Error importing CSV:', error);
        showNotification(error.message || 'Error importing CSV', 'error');
      } finally {
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalText;
        }
      }
    }

    formatDate(dateValue) {
      // Handle null, undefined, empty string, or empty object
      if (!dateValue || (typeof dateValue === 'object' && Object.keys(dateValue).length === 0)) {
        return '';
      }
      
      // If it's already a Date object, use it directly
      if (dateValue instanceof Date) {
        if (isNaN(dateValue.getTime())) {
          return '';
        }
        return dateValue.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      }
      
      // If it's an object with date properties, try to construct a date string
      if (typeof dateValue === 'object' && dateValue !== null) {
        // Try ISO string if available
        if (dateValue.toISOString && typeof dateValue.toISOString === 'function') {
          try {
            const date = new Date(dateValue.toISOString());
            if (!isNaN(date.getTime())) {
              return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
            }
          } catch (e) {
            // Fall through to string conversion
          }
        }
        // If object doesn't have useful date methods, return empty
        return '';
      }
      
      // Convert to string and try parsing
      try {
        const dateString = String(dateValue);
        if (!dateString || dateString.trim() === '') {
          return '';
        }
        const date = new Date(dateString);
        // Check if date is valid
        if (isNaN(date.getTime())) {
          return '';
        }
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
      } catch (error) {
        return '';
      }
    }

    updateItemLineTotal(row) {
      const unitPriceCents = parseInt(row.dataset.unitPriceCents || '0', 10);
      const quantity = parseInt(row.dataset.qty || '1', 10);
      const unitPrice = unitPriceCents / 100;
      const lineTotal = unitPrice * quantity;
      
      const lineTotalEl = row.querySelector('.wishlist-item__line-total');
      if (lineTotalEl && unitPrice > 0) {
        lineTotalEl.textContent = `$${lineTotal.toFixed(2)}`;
      }
    }

    updateTotals() {
      const itemsListEl = this.container.querySelector('.wishlist-detail__items-list');
      const totalsEl = this.container.querySelector('.wishlist-detail__totals');
      const totalUnitsEl = this.container.querySelector('.wishlist-detail__total-units');
      const subtotalEl = this.container.querySelector('.wishlist-detail__subtotal');
      
      if (!itemsListEl || !totalsEl || !totalUnitsEl || !subtotalEl) return;

      const items = itemsListEl.querySelectorAll('.wishlist-item-row');
      let totalUnits = 0;
      let subtotalCents = 0;

      items.forEach(row => {
        const unitPriceCents = parseInt(row.dataset.unitPriceCents || '0', 10);
        const quantity = parseInt(row.dataset.qty || '1', 10);
        
        totalUnits += quantity;
        subtotalCents += unitPriceCents * quantity;
      });

      totalUnitsEl.textContent = totalUnits.toString();
      const subtotal = subtotalCents / 100;
      subtotalEl.textContent = `$${subtotal.toFixed(2)}`;
      
      if (items.length > 0) {
        totalsEl.classList.remove('hidden');
      } else {
        totalsEl.classList.add('hidden');
      }
    }

    escapeHtml(text) {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }

    setupEventListeners() {
      // Item interactions are set up in setupItemInteractions() called after rendering
      // This method handles wishlist-level actions
      
      // Setup edit wishlist name button
      const editNameBtn = this.container.querySelector('.js-edit-wishlist-name');
      if (editNameBtn) {
        editNameBtn.addEventListener('click', () => {
          this.openEditModal();
        });
      }
      
      // Setup modal close handlers and form submission (only attach once)
      const editModal = document.getElementById('wishlist-edit-name-modal');
      if (editModal && !editModal.dataset.listenersAttached) {
        // Setup form submission handler (attach directly to modal form)
        const editForm = editModal.querySelector('.js-edit-wishlist-form');
        if (editForm) {
          // Store reference to this for use in event handler
          const self = this;
          
          // Remove any existing action attribute to prevent normal submission
          if (!editForm.hasAttribute('action')) {
            editForm.setAttribute('action', '#');
          }
          
          // Attach submit handler (use capture phase to catch it early)
          editForm.addEventListener('submit', function(e) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            self.handleEditWishlistSubmit(e.target);
            return false;
          }, true); // Use capture phase
          
          devLog('[WishlistDetail] Edit form submit handler attached');
        } else {
          console.warn('[WishlistDetail] ⚠️ Edit form not found in modal');
        }
        // Close button
        const closeBtn = editModal.querySelector('.wishlist-edit-modal__close');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => {
            editModal.classList.add('hidden');
          });
        }
        
        // Cancel button
        const cancelBtn = editModal.querySelector('.js-edit-wishlist-cancel');
        if (cancelBtn) {
          cancelBtn.addEventListener('click', () => {
            editModal.classList.add('hidden');
          });
        }
        
        // Overlay click
        const overlay = editModal.querySelector('.wishlist-edit-modal__overlay');
        if (overlay) {
          overlay.addEventListener('click', () => {
            editModal.classList.add('hidden');
          });
        }
        
        // ESC key handler (only add once globally)
        if (!window.wishlistEditModalEscHandler) {
          window.wishlistEditModalEscHandler = (e) => {
            if (e.key === 'Escape') {
              const modal = document.getElementById('wishlist-edit-name-modal');
              if (modal && !modal.classList.contains('hidden')) {
                modal.classList.add('hidden');
              }
            }
          };
          document.addEventListener('keydown', window.wishlistEditModalEscHandler);
        }
        
        editModal.dataset.listenersAttached = 'true';
      }
      
      // Setup share button
      const shareBtn = this.container.querySelector('.js-wishlist-share');
      if (shareBtn) {
        shareBtn.addEventListener('click', () => {
          this.shareWishlist();
        });
      }
      
      // Setup delete button
      const deleteBtn = this.container.querySelector('.js-wishlist-delete');
      if (deleteBtn) {
        deleteBtn.addEventListener('click', async () => {
          const confirmed = await showConfirmModal(
            'Delete Wishlist',
            'Are you sure you want to delete this wishlist? This action cannot be undone.'
          );
          if (confirmed) {
            this.deleteWishlist();
          }
        });
      }
      
      // Setup "Add All to Cart" button
      const addAllToCartBtn = this.container.querySelector('.js-add-all-to-cart');
      if (addAllToCartBtn) {
        this.addAllLabel = addAllToCartBtn.dataset.labelAddAll || addAllToCartBtn.textContent.trim() || this.addAllLabel;
        this.addSelectedLabel = addAllToCartBtn.dataset.labelAddSelected || this.addSelectedLabel;
        addAllToCartBtn.addEventListener('click', () => {
          this.handleAddAllToCart();
        });
      }

      const selectAllCheckbox = this.container.querySelector('.js-wishlist-select-all');
      if (selectAllCheckbox) {
        selectAllCheckbox.addEventListener('change', () => {
          const itemIds = this.getCurrentItemIds();
          if (selectAllCheckbox.checked) {
            itemIds.forEach((itemId) => this.selectedItemIds.add(itemId));
          } else {
            this.selectedItemIds.clear();
          }
          this.renderItems();
        });
      }

      // Export wishlist button
      const exportBtn = this.container.querySelector('.js-wishlist-export');
      if (exportBtn) {
        exportBtn.addEventListener('click', async () => {
          await this.exportWishlistToCSV();
        });
      }

      // CSV import button for detail page
      const csvImportBtn = this.container.querySelector('.js-csv-import-detail');
      if (csvImportBtn) {
        csvImportBtn.addEventListener('click', () => {
          const modal = document.getElementById('wishlist-csv-import-modal-detail');
          if (modal) modal.classList.remove('hidden');
        });
      }

      // CSV template download for detail page
      const csvTemplateBtn = this.container.querySelector('.js-csv-download-template-detail');
      if (csvTemplateBtn) {
        csvTemplateBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.downloadCSVTemplate();
        });
      }

      // CSV import modal close for detail page
      const csvImportModal = document.getElementById('wishlist-csv-import-modal-detail');
      if (csvImportModal && !csvImportModal.dataset.listenersAttached) {
        csvImportModal.dataset.listenersAttached = 'true';
        const closeBtn = csvImportModal.querySelector('.wishlist-csv-import-modal__close');
        const cancelBtn = csvImportModal.querySelector('.js-csv-import-cancel-detail');
        const overlay = csvImportModal.querySelector('.wishlist-csv-import-modal__overlay');

        [closeBtn, cancelBtn, overlay].forEach(el => {
          if (el) el.addEventListener('click', () => csvImportModal.classList.add('hidden'));
        });
      }

      // CSV import form submission for detail page
      const csvImportForm = document.querySelector('.js-csv-import-form-detail');
      if (csvImportForm && !csvImportForm.dataset.listenerAdded) {
        csvImportForm.dataset.listenerAdded = 'true';
        csvImportForm.addEventListener('submit', async (e) => {
          e.preventDefault();
          await this.handleCSVImportDetail(csvImportForm);
        });
      }

      this.updateSelectionUi();
    }
    
    async addItemsToServerCart(lines) {
      const scannerCartUrl = new URL('/api/scanner/cart/add', API_URL).toString();
      const requestUrl = `${scannerCartUrl}?shop=${encodeURIComponent(this.shopDomain)}`;
      const chunkSize = 50;
      let cartId = null;

      for (let i = 0; i < lines.length; i += chunkSize) {
        const chunk = lines.slice(i, i + chunkSize);
        const response = await fetch(requestUrl, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            cartId,
            lines: chunk
          })
        });

        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          throw new Error('Server cart endpoint returned invalid response');
        }
        const data = await response.json();

        if (!response.ok || !data?.ok) {
          throw new Error(data?.error || 'Server cart add failed');
        }

        cartId = data.cartId || cartId;
      }
    }

    async addItemsToThemeCartBatch(items, sectionsToRequest) {
      const cartAddUrl = (window.theme?.routes?.cartAdd && window.theme.routes.cartAdd.endsWith('.js'))
        ? window.theme.routes.cartAdd
        : '/cart/add.js';
      const cartRoute = window.theme?.routes?.cart || '/cart';
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json, application/javascript, text/javascript',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: items.map((item) => ({
            id: item.numericVariantId,
            quantity: item.quantity,
            ...(item.stockProperties ? { properties: item.stockProperties } : {})
          })),
          sections: sectionsToRequest.join(','),
          sections_url: cartRoute
        })
      });

      const contentType = response.headers.get('content-type') || '';
      const responseText = await response.text();

      // Shopify may respond with javascript content type while still returning JSON text.
      const isJsonLikeType =
        contentType.includes('application/json') ||
        contentType.includes('application/javascript') ||
        contentType.includes('text/javascript');

      if (!isJsonLikeType) {
        const normalizedBody = String(responseText || '').trim().toLowerCase();
        if (normalizedBody.startsWith('<!doctype') || normalizedBody.startsWith('<html')) {
          throw new Error(`Theme cart add returned HTML response (${response.status})`);
        }
        throw new Error('Theme cart add returned invalid response');
      }

      let data;
      try {
        data = JSON.parse(responseText);
      } catch (error) {
        throw new Error('Theme cart add returned invalid JSON');
      }

      if (!response.ok || data?.status) {
        throw new Error(data?.description || data?.message || 'Failed to add to theme cart');
      }

      return data;
    }

    async handleAddAllToCart() {
      if (!this.wishlist || !this.wishlist.items || this.wishlist.items.length === 0) {
        showNotification('No items to add to cart', 'error');
        return;
      }

      const addAllBtn = this.container.querySelector('.js-add-all-to-cart');
      if (!addAllBtn) return;

      const selectedOnly = this.selectedItemIds.size > 0;
      const selectedSet = new Set(this.selectedItemIds);
      const sourceItems = this.wishlist.items.filter((item) => {
        if (!item.variantId) return false;
        if (!selectedOnly) return true;
        return selectedSet.has(item.id);
      });

      if (sourceItems.length === 0) {
        showNotification('No valid items to add to cart', 'error');
        return;
      }

      const originalText = addAllBtn.textContent;
      addAllBtn.disabled = true;
      addAllBtn.textContent = 'Adding to cart...';
      addAllBtn.classList.add('is-loading');

      let successCount = 0;
      let successQuantity = 0;
      const errors = [];
      try {
        // Ensure selected items are hydrated (price/SKU/MOQ/stock props) before cart add.
        // This avoids cart-page backfill storms for large wishlist adds.
        await this.hydrateItemsFromBackend(sourceItems);

        const validItems = [];
        sourceItems.forEach((item) => {
          const rawVariantId = String(item.variantId || '');
          const variantPart = rawVariantId.includes('/') ? rawVariantId.split('/').pop() : rawVariantId;
          const numericVariantId = parseInt(String(variantPart || '').trim(), 10);
          const quantityInput = this.container.querySelector(`input[name="quantity"][data-item-id="${item.id}"]`);
          const rawQuantity = quantityInput ? parseInt(quantityInput.value, 10) : (item.quantity || 1);
          const itemMoq = getEffectiveMoq(item?.metadata?.moq || item?.metadata?.minimum_order_quantity || 1);
          const normalized = normalizeQtyWithMoq(rawQuantity, itemMoq);
          const quantity = normalized.quantity;
          if (quantityInput) {
            quantityInput.value = String(quantity);
            quantityInput.dataset.initialValue = String(quantity);
          }

          if (!numericVariantId || Number.isNaN(numericVariantId) || numericVariantId <= 0) {
            errors.push(`${item.productTitle || 'Item'}: Invalid variant ID`);
            return;
          }
          if (!Number.isFinite(quantity) || quantity <= 0) {
            errors.push(`${item.productTitle || 'Item'}: Invalid quantity`);
            return;
          }

          validItems.push({
            id: item.id,
            productTitle: item.productTitle,
            numericVariantId,
            quantity,
            merchandiseId: `gid://shopify/ProductVariant/${numericVariantId}`,
            stockProperties: this.getStockTypeProperties(item)
          });
        });

        if (validItems.length === 0) {
          const reason = errors.length > 0 ? errors.join('; ') : 'No valid items found.';
          showNotification(`No items could be added to cart. ${reason}`, 'error');
          return;
        }

        const cartDrawer = document.querySelector('cart-drawer');
        const sectionsToRequest = ['cart-icon-bubble'];
        if (cartDrawer) {
          const drawerSection = cartDrawer.closest('.shopify-section');
          if (drawerSection) {
            const drawerSectionId = drawerSection.id.replace('shopify-section-', '');
            if (drawerSectionId) sectionsToRequest.push(drawerSectionId);
          }
        }

        // Scanner-style backend cart path: create/update server-side cart in batches.
        await this.addItemsToServerCart(
          validItems.map((item) => ({
            merchandiseId: item.merchandiseId,
            quantity: item.quantity
          }))
        );

        // Try batch add; fall back to per-item if the batch is rejected (e.g. an item went OOS).
        let themeCartResponse = null;
        try {
          themeCartResponse = await this.addItemsToThemeCartBatch(validItems, sectionsToRequest);
          successCount = validItems.length;
          successQuantity = validItems.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0);
        } catch (batchErr) {
          console.warn('[WishlistDetail] Batch cart add failed, falling back to per-item:', batchErr.message);
          for (const item of validItems) {
            try {
              themeCartResponse = await this.addItemsToThemeCartBatch([item], sectionsToRequest);
              successCount += 1;
              successQuantity += parseInt(item.quantity, 10) || 0;
            } catch (itemErr) {
              errors.push(`${item.productTitle || 'Item'}: ${itemErr.message || 'Could not be added'}`);
            }
          }
        }

        if (themeCartResponse?.sections && typeof themeCartResponse.sections === 'object') {
          this.updateCartSections(themeCartResponse, cartDrawer);
        } else {
          await this.updateCartAfterAdd();
        }

        const variantsNeedingBackfill = [...new Set(
          validItems
            .filter((item) => !item.stockProperties)
            .map((item) => item.numericVariantId)
        )];
        if (variantsNeedingBackfill.length > 0) {
          setTimeout(() => {
            const cartItems = document.querySelector('cart-items');
            if (cartItems && typeof cartItems.backfillStockTypeProperties === 'function') {
              cartItems.backfillStockTypeProperties({
                variantIds: variantsNeedingBackfill,
                force: true
              });
            }
          }, 500);
        }

        if (errors.length > 0) {
          showNotification(`Added ${successQuantity} unit${successQuantity !== 1 ? 's' : ''} to cart (${successCount} line${successCount !== 1 ? 's' : ''}). ${errors.length} item${errors.length > 1 ? 's' : ''} skipped.`, 'warning');
        } else {
          showNotification(`Successfully added ${successQuantity} unit${successQuantity !== 1 ? 's' : ''} to cart`, 'success');
        }
      } catch (error) {
        console.error('[WishlistDetail] Error in handleAddAllToCart:', error);
        const errorMessage = error?.message || 'Error adding items to cart. Please try again.';
        showNotification(errorMessage, 'error');
      } finally {
        addAllBtn.disabled = false;
        addAllBtn.classList.remove('is-loading');
        addAllBtn.textContent = originalText;
        this.updateSelectionUi();
      }
    }
    
    async updateCartAfterAdd() {
      try {
        console.log('[WishlistDetail] Starting cart update...');
        
        // Get cart drawer if it exists
        const cartDrawer = document.querySelector('cart-drawer');
        
        // Collect sections we need (same as cart-drawer.js)
        const sectionsToFetch = ['cart-icon-bubble'];
        if (cartDrawer) {
          const drawerSection = cartDrawer.closest('.shopify-section');
          if (drawerSection) {
            const drawerSectionId = drawerSection.id.replace('shopify-section-', '');
            if (drawerSectionId) {
              sectionsToFetch.push(drawerSectionId);
            }
          }
        }
        
        // Use cart route to fetch sections (avoids page wrapping issues)
        // The cart route returns sections directly, not wrapped in {page: {...}}
        const cartRoute = window.theme?.routes?.cart || '/cart';
        const sectionsUrl = `${cartRoute}?sections=${sectionsToFetch.join(',')}`;
        console.log('[WishlistDetail] Fetching sections from cart route:', sectionsUrl);
        
        const response = await fetch(sectionsUrl, {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin'
        });
        
        if (!response.ok) {
          throw new Error(`Failed to fetch cart sections: ${response.status} ${response.statusText}`);
        }
        
        const sectionsData = await response.json();
        console.log('[WishlistDetail] Received sections response, top-level keys:', Object.keys(sectionsData));
        
        // Extract sections - handle different response structures
        let sections = null;
        
        // Case 1: Direct sections (cart route usually returns this)
        if (sectionsData['cart-icon-bubble'] || Object.keys(sectionsData).some(key => 
          key.includes('cart-icon') || key.includes('shopify-section') || key.includes('cart-drawer') || key.startsWith('sections--')
        )) {
          sections = sectionsData;
          console.log('[WishlistDetail] Found direct sections');
        }
        // Case 2: Wrapped in sections property
        else if (sectionsData.sections && typeof sectionsData.sections === 'object' && !Array.isArray(sectionsData.sections)) {
          sections = sectionsData.sections;
          console.log('[WishlistDetail] Found sections in .sections property');
        }
        // Case 3: Wrapped in page object (common when fetching from page routes like /pages/wishlist)
        else if (sectionsData.page && typeof sectionsData.page === 'object') {
          const pageObj = sectionsData.page;
          const pageKeys = Object.keys(pageObj);
          console.log('[WishlistDetail] Found page object with keys:', pageKeys);
          
          // Log the actual values to see what we're dealing with
          pageKeys.forEach(key => {
            const value = pageObj[key];
            if (typeof value === 'string') {
              console.log(`[WishlistDetail] Page key "${key}": string (length: ${value.length})`);
            } else if (typeof value === 'object') {
              console.log(`[WishlistDetail] Page key "${key}": object with keys:`, Object.keys(value || {}));
            }
          });
          
          // Check if page.sections exists (nested structure)
          if (pageObj.sections && typeof pageObj.sections === 'object' && !Array.isArray(pageObj.sections)) {
            sections = pageObj.sections;
            console.log('[WishlistDetail] Found sections in page.sections:', Object.keys(sections));
          }
          // Check if sections are directly in page object (page keys are section IDs)
          // Look for any key that might be a section ID
          else if (pageKeys.length > 0) {
            // If page object has string values, those are likely section HTML
            const hasStringValues = pageKeys.some(key => typeof pageObj[key] === 'string');
            // Or if keys look like section IDs
            const hasSectionLikeKeys = pageKeys.some(key => 
              key.includes('cart-icon') || 
              key.includes('shopify-section') || 
              key.includes('cart-drawer') || 
              key.startsWith('sections--') ||
              key === 'cart-icon-bubble'
            );
            
            if (hasStringValues || hasSectionLikeKeys || pageKeys.length > 0) {
              sections = pageObj;
              console.log('[WishlistDetail] Using page object as sections. It contains:', {
                keys: pageKeys,
                hasStringValues,
                hasSectionLikeKeys
              });
            }
          }
        }
        
        if (!sections) {
          console.error('[WishlistDetail] ❌ Could not extract sections from response. Full structure:', {
            topKeys: Object.keys(sectionsData),
            hasPage: !!sectionsData.page,
            pageKeys: sectionsData.page ? Object.keys(sectionsData.page) : null,
            sample: JSON.stringify(sectionsData).substring(0, 500)
          });
          // Try fallback methods
          await this.refreshCartIconFallback();
          if (cartDrawer && typeof cartDrawer.refresh === 'function') {
            await cartDrawer.refresh(true);
          }
          return;
        }
        
        devLog('[WishlistDetail] Extracted sections');
        
        // Update cart icon bubble
        if (sections['cart-icon-bubble']) {
          const cartIconBubble = document.getElementById('cart-icon-bubble');
          if (cartIconBubble) {
            let bubbleHtml = sections['cart-icon-bubble'];
            // Extract inner HTML if wrapped in outer div
            if (typeof bubbleHtml === 'string' && bubbleHtml.includes('<div id="cart-icon-bubble">')) {
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = bubbleHtml;
              const innerBubble = tempDiv.querySelector('#cart-icon-bubble');
              bubbleHtml = innerBubble ? innerBubble.innerHTML : bubbleHtml;
            }
            cartIconBubble.innerHTML = bubbleHtml;
            devLog('[WishlistDetail] Updated cart icon bubble');
          }
        } else {
          console.warn('[WishlistDetail] cart-icon-bubble not in sections, using fallback');
          await this.refreshCartIconFallback();
        }
        
        // Update cart drawer using renderContents (same as product-form.js)
        if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
          const drawerSection = cartDrawer.closest('.shopify-section');
          if (drawerSection) {
            const drawerSectionId = drawerSection.id.replace('shopify-section-', '');
            if (drawerSectionId && sections[drawerSectionId]) {
              cartDrawer.renderContents({ sections: sections }, false);
              devLog('[WishlistDetail] Updated cart drawer');
            } else {
              console.warn('[WishlistDetail] Drawer section not in response. Looking for:', drawerSectionId, 'Available:', Object.keys(sections));
              // Try using cart drawer's refresh method as fallback
              if (typeof cartDrawer.refresh === 'function') {
                await cartDrawer.refresh(true);
                devLog('[WishlistDetail] Used cart drawer refresh() as fallback');
              }
            }
          }
        } else if (cartDrawer && typeof cartDrawer.refresh === 'function') {
          // Fallback: use refresh method
          await cartDrawer.refresh(true);
          devLog('[WishlistDetail] Used cart drawer refresh() method');
        }
        
        // Dispatch events for other listeners
        document.dispatchEvent(new CustomEvent('on:cart:update', { 
          bubbles: true,
          detail: { sections: sections }
        }));
        
        document.dispatchEvent(new CustomEvent('on:cart:add', { 
          bubbles: true,
          detail: { sections: sections }
        }));
        
        devLog('[WishlistDetail] Cart update complete');
      } catch (error) {
        console.error('[WishlistDetail] ❌ Error updating cart:', error);
        // Try fallback methods
        await this.refreshCartIconFallback();
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && typeof cartDrawer.refresh === 'function') {
          try {
            await cartDrawer.refresh(true);
            devLog('[WishlistDetail] Used cart drawer refresh() as error fallback');
          } catch (refreshError) {
            console.error('[WishlistDetail] ❌ Cart drawer refresh also failed:', refreshError);
          }
        }
      }
    }
    
    updateCartSections(responseData, cartDrawer) {
      // Handle different response structures from Shopify sections API:
      // 1. Direct sections: { 'cart-icon-bubble': '...', 'section-id': '...' }
      // 2. Wrapped in page object: { page: { 'cart-icon-bubble': '...', 'section-id': '...' } }
      // 3. Wrapped in page.sections: { page: { sections: { 'cart-icon-bubble': '...' } } }
      // 4. Wrapped in sections: { sections: { 'cart-icon-bubble': '...' } }
      // 5. Full cart/add.js response: { sections: { ... }, items: [...], etc. }
      let sections = null;
      
      // Check if wrapped in page object FIRST (common for page routes like /pages/wishlist)
      // When fetching from page routes, Shopify wraps sections in {page: {...}}
      if (responseData.page && typeof responseData.page === 'object') {
        const pageObj = responseData.page;
        const pageKeys = Object.keys(pageObj);
        
        // Check if page.sections exists (nested structure)
        if (pageObj.sections && typeof pageObj.sections === 'object' && !Array.isArray(pageObj.sections)) {
          sections = pageObj.sections;
          console.log('[WishlistDetail] Found sections in responseData.page.sections:', Object.keys(sections));
        }
        // Check if sections are directly in page object (sections are keys of page object itself)
        else if (pageObj['cart-icon-bubble'] || pageKeys.some(key => key.includes('shopify-section') || key.includes('cart-icon') || key.includes('cart-drawer') || key.startsWith('sections--'))) {
          sections = pageObj;
          console.log('[WishlistDetail] Found sections directly in responseData.page:', pageKeys);
        }
        // Last resort: use the entire page object if it has multiple keys (might be sections)
        else if (pageKeys.length > 0) {
          // Log what we found for debugging
          console.log('[WishlistDetail] Page object keys (might be sections):', pageKeys);
          // Try using page object anyway - sometimes Shopify returns sections with different key format
          sections = pageObj;
        }
      }
      // Check for direct sections object (most common for root/cart routes)
      else if (responseData['cart-icon-bubble'] || Object.keys(responseData).some(key => key.includes('shopify-section') || key.includes('cart-icon') || key.includes('cart-drawer') || key.startsWith('sections--'))) {
        sections = responseData;
        console.log('[WishlistDetail] Found direct sections in response');
      }
      // Check if wrapped in sections property
      else if (responseData.sections && typeof responseData.sections === 'object' && !Array.isArray(responseData.sections)) {
        sections = responseData.sections;
        console.log('[WishlistDetail] Found sections in responseData.sections');
      }
      
      if (!sections) {
        console.warn('[WishlistDetail] No sections found in response data. Response structure:', {
          topLevelKeys: Object.keys(responseData),
          hasPage: !!responseData.page,
          pageType: responseData.page ? typeof responseData.page : null,
          pageKeys: responseData.page && typeof responseData.page === 'object' ? Object.keys(responseData.page) : null,
          hasPageSections: responseData.page && responseData.page.sections ? Object.keys(responseData.page.sections) : null,
          sample: JSON.stringify(responseData).substring(0, 1000)
        });
        
        // If we have a page object, try to use it anyway - it might contain sections with different keys
        if (responseData.page && typeof responseData.page === 'object') {
          const pageKeys = Object.keys(responseData.page);
          console.log('[WishlistDetail] Attempting to use page object as sections. Page keys:', pageKeys);
          // Use page object directly - it might work even if keys don't match exactly
          sections = responseData.page;
        } else {
          // No sections found, use cart drawer refresh as fallback
          console.log('[WishlistDetail] Using cart drawer refresh() as fallback');
          if (cartDrawer && typeof cartDrawer.refresh === 'function') {
            cartDrawer.refresh();
            // Still try to update cart icon using fallback method
            this.refreshCartIconFallback();
          }
          return;
        }
      }
      
      console.log('[WishlistDetail] Extracted sections:', Object.keys(sections));
      
      // Update cart icon bubble (same as ProductForm.updateCartIcon)
      if (sections['cart-icon-bubble']) {
        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble) {
          let bubbleHtml = sections['cart-icon-bubble'];
          
          // Handle case where section might be wrapped in additional HTML
          // Try to extract just the inner content if it's wrapped
          if (typeof bubbleHtml === 'string') {
            const tempDiv = document.createElement('div');
            tempDiv.innerHTML = bubbleHtml;
            const innerBubble = tempDiv.querySelector('#cart-icon-bubble');
            if (innerBubble) {
              bubbleHtml = innerBubble.innerHTML;
            }
          }
          
          cartIconBubble.innerHTML = bubbleHtml;
          console.log('[WishlistDetail] Updated cart icon bubble successfully');
          
          // Also try to trigger any shake animation if present
          const cartIcon = document.getElementById('cart-icon');
          if (cartIcon && cartIcon.classList.contains('header__icon--cart-shake')) {
            cartIcon.classList.add('has-motion');
          }
        } else {
          console.error('[WishlistDetail] Cart icon bubble element not found in DOM');
          console.log('[WishlistDetail] Available elements with "cart" in ID:', 
            Array.from(document.querySelectorAll('[id*="cart"]')).map(el => el.id));
        }
      } else {
        console.warn('[WishlistDetail] cart-icon-bubble section not in response. Available sections:', Object.keys(sections));
      }
      
      // Update cart drawer if it exists (same approach as product-form.js)
      if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
        // Find the drawer section ID
        const drawerSection = cartDrawer.closest('.shopify-section');
        if (drawerSection) {
          const drawerSectionId = drawerSection.id.replace('shopify-section-', '');
          if (drawerSectionId) {
            // renderContents expects { sections: { 'section-id': 'html', ... } }
            cartDrawer.renderContents({ sections: sections }, false);
            console.log('[WishlistDetail] Updated cart drawer with section:', drawerSectionId);
          } else {
            console.warn('[WishlistDetail] Could not find drawer section ID');
          }
        } else {
          console.warn('[WishlistDetail] Cart drawer section element not found');
        }
      } else if (cartDrawer && typeof cartDrawer.refresh === 'function') {
        // Fallback: use refresh method
        cartDrawer.refresh();
        console.log('[WishlistDetail] Refreshed cart drawer using refresh()');
      }
      
      // Dispatch cart update events for other listeners (same as product-form.js)
      document.dispatchEvent(new CustomEvent('on:cart:update', { 
        bubbles: true,
        detail: { sections: sections }
      }));
      
      document.dispatchEvent(new CustomEvent('on:cart:add', { 
        bubbles: true,
        detail: { sections: sections }
      }));
    }
    
    async refreshCartIconFallback() {
      // Fallback: try to fetch cart.json and manually update the count
      // This is the most reliable method as cart.json always works
      try {
        console.log('[WishlistDetail] Using cart.json fallback method to update cart count');
        const cartRoute = window.theme?.routes?.cart || '/cart';
        const cartResponse = await fetch(`${cartRoute}.js`, {
          headers: {
            Accept: 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin'
        });
        
        if (cartResponse.ok) {
          const cartData = await cartResponse.json();
          const itemCount = cartData.item_count || 0;
          console.log('[WishlistDetail] Cart data from fallback - item_count:', itemCount);
          
          const cartIconBubble = document.getElementById('cart-icon-bubble');
          if (cartIconBubble) {
            // Find or create count element
            let countBubble = cartIconBubble.querySelector('.header__cart-count');
            let countElement = countBubble ? countBubble.querySelector('.header__cart-count-number') : null;
            
            if (!countBubble) {
              // Create count bubble if it doesn't exist
              countBubble = document.createElement('div');
              countBubble.className = 'header__cart-count absolute bg-theme-text text-theme-bg text-center';
              cartIconBubble.appendChild(countBubble);
            }
            
            if (!countElement) {
              countElement = document.createElement('span');
              countElement.className = 'header__cart-count-number';
              countElement.setAttribute('aria-hidden', 'true');
              countBubble.appendChild(countElement);
              
              // Add visually hidden text
              const visuallyHidden = document.createElement('span');
              visuallyHidden.className = 'visually-hidden';
              countBubble.appendChild(visuallyHidden);
            }
            
            // Update count
            if (itemCount > 0 && itemCount < 10000) {
              countElement.textContent = itemCount;
              countBubble.style.display = '';
              
              // Update visually hidden text
              const visuallyHidden = countBubble.querySelector('.visually-hidden');
              if (visuallyHidden) {
                visuallyHidden.textContent = `Cart count: ${itemCount}`;
              }
            } else {
              countBubble.style.display = 'none';
            }
            
            console.log('[WishlistDetail] Successfully updated cart count to:', itemCount);
          } else {
            console.error('[WishlistDetail] Cart icon bubble element (#cart-icon-bubble) not found');
            console.log('[WishlistDetail] Available cart-related elements:', 
              Array.from(document.querySelectorAll('[id*="cart"], [class*="cart"]')).map(el => ({
                id: el.id,
                class: el.className.substring(0, 50)
              })).slice(0, 10));
          }
        } else {
          console.error('[WishlistDetail] Failed to fetch cart.json:', cartResponse.status, cartResponse.statusText);
        }
      } catch (error) {
        console.error('[WishlistDetail] Fallback cart refresh failed:', error);
      }
    }
    
    async shareWishlist() {
      if (!this.wishlist || !this.wishlistId || !this.customerId) {
        showNotification('Unable to share wishlist. Please try again.', 'error');
        return;
      }

      // Open share modal
      const modal = document.getElementById('wishlist-share-modal');
      if (!modal) {
        showNotification('Share modal not found', 'error');
        return;
      }

      modal.classList.remove('hidden');
      
      // Populate modal with current wishlist data
      const shareLinkInput = document.getElementById('share-link');
      const isPublicToggle = document.getElementById('is-public-toggle');
      const shareLinkSection = document.querySelector('.js-share-link-section');
      
      const isPublic = this.wishlist.isPublic || false;
      
      if (isPublicToggle) {
        isPublicToggle.checked = isPublic;
      }
      
      // Show/hide share link section based on public status
      if (shareLinkSection) {
        if (isPublic && this.wishlist.publicShareToken) {
          shareLinkSection.classList.remove('hidden');
          if (shareLinkInput) {
            const shareUrl = `${window.location.origin}/pages/wishlist-share?token=${this.wishlist.publicShareToken}`;
            shareLinkInput.value = shareUrl;
          }
        } else {
          shareLinkSection.classList.add('hidden');
          if (shareLinkInput) {
            shareLinkInput.value = '';
          }
        }
      }

      // Setup modal event listeners if not already set up
      if (!modal.dataset.listenersAttached) {
        // Close button
        const closeBtn = modal.querySelector('.wishlist-share-modal__close');
        if (closeBtn) {
          closeBtn.addEventListener('click', () => {
            modal.classList.add('hidden');
          });
        }

        // Overlay click to close
        const overlay = modal.querySelector('.wishlist-share-modal__overlay');
        if (overlay) {
          overlay.addEventListener('click', () => {
            modal.classList.add('hidden');
          });
        }

        // Done button
        const doneBtn = modal.querySelector('.wishlist-share-modal__done');
        if (doneBtn) {
          doneBtn.addEventListener('click', () => {
            modal.classList.add('hidden');
          });
        }

        // Copy link button
        const copyBtn = modal.querySelector('.js-copy-share-link');
        if (copyBtn) {
          copyBtn.addEventListener('click', async () => {
            if (shareLinkInput && shareLinkInput.value) {
              try {
                await navigator.clipboard.writeText(shareLinkInput.value);
                copyBtn.textContent = 'Copied!';
                setTimeout(() => {
                  copyBtn.textContent = 'Copy Link';
                }, 2000);
                showNotification('Share link copied to clipboard!', 'success');
              } catch (error) {
                console.error('Failed to copy link:', error);
                shareLinkInput.select();
                showNotification('Please copy the link manually', 'info');
              }
            } else {
              showNotification('No share link available. Make the wishlist public first.', 'warning');
            }
          });
        }

        // Toggle public status
        if (isPublicToggle) {
          isPublicToggle.addEventListener('change', async (e) => {
            const isPublic = e.target.checked;
            
            // Optimistically update visibility (will be updated again after API call)
            const shareLinkSectionEl = document.querySelector('.js-share-link-section');
            const shareLinkInputEl = document.getElementById('share-link');
            if (shareLinkSectionEl) {
              if (isPublic && this.wishlist.publicShareToken) {
                shareLinkSectionEl.classList.remove('hidden');
                if (shareLinkInputEl) {
                  const shareUrl = `${window.location.origin}/pages/wishlist-share?token=${this.wishlist.publicShareToken}`;
                  shareLinkInputEl.value = shareUrl;
                }
              } else {
                shareLinkSectionEl.classList.add('hidden');
                if (shareLinkInputEl) {
                  shareLinkInputEl.value = '';
                }
              }
            }
            
            await this.updateWishlistPublicStatus(isPublic);
          });
        }

        modal.dataset.listenersAttached = 'true';
      }
    }

    openEditModal() {
      const modal = document.getElementById('wishlist-edit-name-modal');
      if (!modal || !this.wishlist) return;
      
      // Populate form fields with current wishlist data
      const nameInput = modal.querySelector('#edit-wishlist-name');
      const descInput = modal.querySelector('#edit-wishlist-description');
      
      if (nameInput) {
        nameInput.value = this.wishlist.name || '';
      }
      
      if (descInput) {
        descInput.value = this.wishlist.description || '';
      }
      
      // Show modal
      modal.classList.remove('hidden');
    }
    
    async handleEditWishlistSubmit(form) {
      if (!this.wishlistId || !this.customerId) {
        showNotification('Unable to update wishlist. Please refresh the page.', 'error');
        return;
      }
      
      const nameInput = form.querySelector('#edit-wishlist-name');
      const descInput = form.querySelector('#edit-wishlist-description');
      const submitBtn = form.querySelector('button[type="submit"]');
      
      if (!nameInput) {
        showNotification('Name field is required', 'error');
        return;
      }
      
      const name = nameInput.value.trim();
      const description = descInput ? descInput.value.trim() : '';
      
      if (!name) {
        showNotification('Wishlist name is required', 'error');
        nameInput.focus();
        return;
      }
      
      // Disable submit button
      const originalText = submitBtn ? submitBtn.textContent : 'Save Changes';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Saving...';
      }
      
      try {
        const response = await fetch(getApiUrl(`/${this.wishlistId}`, { customer_id: this.customerId }), {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            customerId: this.customerId,
            name: name,
            description: description || null,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Failed to update wishlist' }));
          throw new Error(errorData.error || 'Failed to update wishlist');
        }

        const updatedWishlist = await response.json();
        this.wishlist = updatedWishlist;

        // Update the displayed title and description
        const titleEl = this.container.querySelector('.wishlist-detail__title-text');
        if (titleEl) {
          titleEl.textContent = updatedWishlist.name || 'Untitled Wishlist';
        }

        const descEl = this.container.querySelector('.wishlist-detail__description-text');
        if (descEl) {
          descEl.textContent = updatedWishlist.description || '';
          // Hide description element if empty
          const descContainer = this.container.querySelector('.wishlist-detail__description');
          if (descContainer) {
            if (!updatedWishlist.description || updatedWishlist.description.trim() === '') {
              descContainer.style.display = 'none';
            } else {
              descContainer.style.display = '';
            }
          }
        }

        // Close modal
        const modal = document.getElementById('wishlist-edit-name-modal');
        if (modal) {
          modal.classList.add('hidden');
        }

        showNotification('Wishlist updated successfully', 'success');
      } catch (error) {
        console.error('[WishlistDetail] Error updating wishlist:', error);
        showNotification(error.message || 'Failed to update wishlist', 'error');
      } finally {
        // Re-enable submit button
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalText;
        }
      }
    }
    
    async updateWishlistPublicStatus(isPublic) {
      if (!this.wishlistId || !this.customerId) return;

      try {
        const response = await fetch(getApiUrl(`/${this.wishlistId}`, { customer_id: this.customerId }), {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            customerId: this.customerId,
            isPublic: isPublic,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Failed to update wishlist' }));
          throw new Error(errorData.error || 'Failed to update wishlist');
        }

        const updatedWishlist = await response.json();
        this.wishlist = updatedWishlist;

        // Update share link section visibility and content
        const shareLinkInput = document.getElementById('share-link');
        const shareLinkSection = document.querySelector('.js-share-link-section');
        const isPublicToggle = document.getElementById('is-public-toggle');
        
        this.updateShareLinkVisibility(isPublicToggle, shareLinkSection, shareLinkInput, updatedWishlist);

        showNotification(
          isPublic ? 'Wishlist is now public! Share the link below.' : 'Wishlist is now private.',
          'success'
        );
      } catch (error) {
        console.error('[WishlistDetail] Error updating public status:', error);
        showNotification(error.message || 'Failed to update wishlist sharing status', 'error');
        
        // Revert toggle on error
        const isPublicToggle = document.getElementById('is-public-toggle');
        if (isPublicToggle) {
          isPublicToggle.checked = !isPublic;
        }
      }
    }

    updateShareLinkVisibility(toggle, section, input, wishlistData = null) {
      if (!toggle || !section || !input) return;
      
      const isPublic = toggle.checked;
      const wishlist = wishlistData || this.wishlist;
      
      if (isPublic && wishlist && wishlist.publicShareToken) {
        section.classList.remove('hidden');
        const shareUrl = `${window.location.origin}/pages/wishlist-share?token=${wishlist.publicShareToken}`;
        input.value = shareUrl;
      } else if (isPublic && wishlist && !wishlist.publicShareToken) {
        // If making public but token not yet generated (shouldn't happen, but handle gracefully)
        section.classList.add('hidden');
        input.value = '';
      } else {
        section.classList.add('hidden');
        input.value = '';
      }
    }

    async deleteWishlist() {
      if (!this.wishlistId || !this.customerId) {
        showNotification('Unable to delete wishlist. Please try again.', 'error');
        return;
      }

      const confirmed = await showConfirmModal(
        'Delete Wishlist',
        'Are you sure you want to delete this wishlist? This action cannot be undone.'
      );
      if (!confirmed) {
        return;
      }

      try {
        const response = await fetch(getApiUrl(`/${this.wishlistId}`, { customer_id: this.customerId }), {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ customerId: this.customerId }),
          credentials: 'include',
        });

        if (!response.ok) {
          const errorText = await response.text();
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch (e) {
            errorData = { error: errorText || 'Failed to delete wishlist' };
          }
          throw new Error(errorData.error || 'Failed to delete wishlist');
        }

        showNotification('Wishlist deleted successfully', 'success');
        
        // Redirect to wishlists list page after a delay
        setTimeout(() => {
          window.location.href = '/pages/my-wishlist';
        }, 1000);
      } catch (error) {
        console.error('[WishlistDetail] Error deleting wishlist:', error);
        showNotification(error.message || 'Failed to delete wishlist', 'error');
      }
    }
  }

  // Add to Wishlist functionality for product cards
  class AddToWishlist {
    constructor() {
      this.customerId = null;
      this.wishlists = [];
      this.currentProductData = null;
      
      // Try to get customer ID from a button's data attribute first (most reliable on collection pages)
      const firstButton = document.querySelector('.js-add-to-wishlist');
      const customerIdFromButton = firstButton?.dataset.customerId || null;
      
      // Fallback to global CUSTOMER_ID
      this.customerId = customerIdFromButton || CUSTOMER_ID;
      
      // Customer ID initialization (logging removed for performance)
      
      // If no customer ID initially, still initialize - we'll get it from buttons when clicked
      // This allows the class to work even if customer ID isn't available at initialization time
      this.init();
      
      // Only load wishlists if we have a customer ID
      if (this.customerId) {
        this.loadWishlists();
      }
    }

    async loadWishlists() {
      try {
        const url = getApiUrl('', { customer_id: this.customerId });
        const response = await fetch(url);
        if (response.ok) {
          const data = await response.json();
          this.wishlists = data.wishlists || [];
        }
      } catch (error) {
        console.error('[Wishlist] Error loading wishlists:', error);
      }
    }

    init() {
      // Prevent multiple event listeners
      if (this.clickHandler) {
        return;
      }
      
      // Use capture phase to ensure we handle the event before other handlers
      this.clickHandler = (e) => {
        // Check if the click is on a wishlist button or its child elements
        const addBtn = e.target.closest('.js-add-to-wishlist');
        if (addBtn) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation(); // Stop all other handlers
          this.handleAddToWishlist(addBtn);
          return false;
        }
      };
      document.addEventListener('click', this.clickHandler, true); // Use capture phase
      
      // Track button count for MutationObserver
      const buttons = document.querySelectorAll('.js-add-to-wishlist');
      this.buttonCount = buttons.length;
      
      // Watch for dynamically added buttons (e.g., from AJAX-loaded product cards)
      // Safari/iOS performance: Debounce and scope observer to prevent excessive logging
      if (typeof MutationObserver !== 'undefined') {
        let lastButtonCount = buttons.length;
        let buttonCountCheckTimeout = null;
        
        // Debounced function to check button count changes
        const checkButtonCount = () => {
          const currentButtons = document.querySelectorAll('.js-add-to-wishlist');
          const currentCount = currentButtons.length;
          
          // Only update if count actually changed
          if (currentCount !== lastButtonCount) {
            devLog('[AddToWishlist] Buttons changed: now', currentCount, '(was', lastButtonCount, ')');
            lastButtonCount = currentCount;
          }
        };
        
        const debouncedCheck = () => {
          if (buttonCountCheckTimeout) clearTimeout(buttonCountCheckTimeout);
          buttonCountCheckTimeout = setTimeout(checkButtonCount, 500); // Debounce by 500ms
        };
        
        const observer = new MutationObserver(debouncedCheck);
        
        // Scope observer to main content area instead of entire body for better performance
        const mainContent = document.getElementById('main-content') || document.body;
        observer.observe(mainContent, {
          childList: true,
          subtree: true
        });
        
        // Store observer reference for cleanup if needed
        this.mutationObserver = observer;
      }
      
      // Store initial button count
      this.buttonCount = buttons.length;
    }

    async handleAddToWishlist(button) {
      // Get customer ID from button's data attribute (available on collection pages)
      const customerIdFromButton = button.dataset.customerId || null;
      if (customerIdFromButton && (!this.customerId || this.customerId !== customerIdFromButton)) {
        this.customerId = customerIdFromButton;
        console.log('[AddToWishlist] Updated customerId from button:', this.customerId);
        // Load wishlists now that we have a customer ID
        await this.loadWishlists();
      }
      
      if (!this.customerId) {
        console.warn('[AddToWishlist] No customer ID available');
        showNotification('Please log in to add items to your wishlist', 'error');
        return;
      }
      
      const productId = button.dataset.productId;
      const variantId = button.dataset.variantId;
      const wishlistId = button.dataset.wishlistId;

      if (!productId || !variantId) {
        showNotification('Product information missing', 'error');
        return;
      }

      // Get product details from button data attributes
      const productHandle = button.dataset.productHandle || productId.replace('gid://shopify/Product/', '');
      const productTitle = button.dataset.productTitle || 'Product';
      const variantTitle = button.dataset.variantTitle || '';
      const imageUrl = button.dataset.imageUrl || null;
      // Price is already in dollars (decimal format) from Liquid
      const price = button.dataset.price ? parseFloat(button.dataset.price) : null;

      // Get quantity from button's data-moq attribute (for PLP, quantity defaults to MOQ)
      const moq = button.dataset.moq ? parseInt(button.dataset.moq) : 1;
      
      // Store current product data for wishlist selector
      this.currentProductData = {
        productId,
        variantId,
        productHandle,
        productTitle,
        variantTitle,
        imageUrl,
        price,
        quantity: moq // Use MOQ as default quantity for PLP buttons
      };

      // If wishlist ID is provided, add to that specific wishlist
      if (wishlistId) {
        await this.addItemToWishlist(wishlistId, this.currentProductData);
      } else {
        // Show wishlist selector modal
        await this.showWishlistSelector();
      }
    }

    async createDefaultWishlist() {
      try {
        console.log('[AddToWishlist] Creating default wishlist...');
        const idempotencyKey = `wishlist-default-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        const response = await fetch(getApiUrl(''), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            customerId: this.customerId,
            name: 'My Wishlist',
            description: 'Default wishlist',
            idempotencyKey
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to create wishlist');
        }

        const wishlist = await response.json();
        console.log('[AddToWishlist] Default wishlist created:', wishlist);
        
        // Reload wishlists to include the new one
        await this.loadWishlists();
        
        return wishlist;
      } catch (error) {
        console.error('[AddToWishlist] Error creating default wishlist:', error);
        throw error;
      }
    }

    async showWishlistSelector() {
      // Reload wishlists to get latest
      await this.loadWishlists();

      if (this.wishlists.length === 0) {
        // Automatically create a default wishlist instead of asking
        try {
          showNotification('Creating your first wishlist...', 'info');
          const newWishlist = await this.createDefaultWishlist();
          showNotification('Wishlist created! Adding item...', 'success');
          
          // Now add the item to the newly created wishlist
          if (this.currentProductData) {
            await this.addItemToWishlist(newWishlist.id, this.currentProductData);
          }
          return;
        } catch (error) {
          console.error('[AddToWishlist] Failed to create default wishlist:', error);
          showNotification('Failed to create wishlist. Please try again.', 'error');
          return;
        }
      }

      // Create selector modal
      const modal = document.createElement('div');
      modal.className = 'wishlist-selector-modal';
      modal.style.cssText = `
        position: fixed;
        inset: 0;
        z-index: 10000;
        display: flex;
        align-items: center;
        justify-content: center;
        background: rgba(0, 0, 0, 0.5);
      `;

      const content = document.createElement('div');
      content.style.cssText = `
        background: #FFFFFF;
        border-radius: 8px;
        padding: 2rem;
        max-width: 500px;
        width: 90%;
        max-height: 80vh;
        overflow-y: auto;
        box-shadow: 0 4px 24px rgba(0, 0, 0, 0.3);
      `;

      content.innerHTML = `
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 1.5rem;">
          <h2 style="font-size: 1.25rem; font-weight: 600; margin: 0;">Add to Wishlist</h2>
          <button type="button" class="wishlist-selector-close" style="padding: 0.5rem; background: transparent; border: none; cursor: pointer;">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>
        <div class="wishlist-selector-list" style="display: flex; flex-direction: column; gap: 0.5rem;">
          ${this.wishlists.map(w => `
            <button type="button" class="wishlist-selector-item" data-wishlist-id="${w.id}" style="
              padding: 1rem;
              text-align: left;
              background: #F6F6F7;
              border: 1px solid #E1E3E5;
              border-radius: 6px;
              cursor: pointer;
              transition: all 0.2s;
            ">
              <div style="font-weight: 600; margin-bottom: 0.25rem;">${this.escapeHtml(w.name)}</div>
              <div style="font-size: 0.875rem; color: #6D7175;">${w.itemCount || 0} items</div>
            </button>
          `).join('')}
        </div>
        <div style="margin-top: 1.5rem; padding-top: 1.5rem; border-top: 1px solid #E1E3E5;">
          <button type="button" class="btn btn--primary" style="width: 100%;" onclick="window.location.href='/pages/my-wishlist'">
            Create New Wishlist
          </button>
        </div>
      `;

      modal.appendChild(content);
      document.body.appendChild(modal);

      // Close handler
      const closeBtn = content.querySelector('.wishlist-selector-close');
      const closeModal = () => {
        modal.remove();
        this.currentProductData = null;
      };

      closeBtn.addEventListener('click', closeModal);
      modal.addEventListener('click', (e) => {
        if (e.target === modal) closeModal();
      });

      // Wishlist item selection
      content.querySelectorAll('.wishlist-selector-item').forEach(item => {
        item.addEventListener('click', async () => {
          const wishlistId = item.dataset.wishlistId;
          console.log('[AddToWishlist] Wishlist clicked, wishlistId:', wishlistId, 'customerId:', this.customerId, 'currentProductData:', this.currentProductData);
          if (!wishlistId) {
            console.error('[AddToWishlist] No wishlist ID found');
            showNotification('Error: No wishlist selected', 'error');
            return;
          }
          if (!this.customerId) {
            console.error('[AddToWishlist] No customer ID available');
            showNotification('Error: Please log in to add items to your wishlist', 'error');
            return;
          }
          if (!this.currentProductData) {
            console.error('[AddToWishlist] No product data available');
            showNotification('Error: Product information missing', 'error');
            return;
          }
          // Save product data before closing modal (closeModal sets currentProductData to null)
          const productData = { ...this.currentProductData };
          closeModal();
          try {
            await this.addItemToWishlist(wishlistId, productData);
          } catch (error) {
            console.error('[AddToWishlist] Error adding item to wishlist:', error);
            // Error notification is shown in addItemToWishlist
          }
        });

        item.addEventListener('mouseenter', function() {
          this.style.backgroundColor = '#E1E3E5';
          this.style.borderColor = '#008060';
        });

        item.addEventListener('mouseleave', function() {
          this.style.backgroundColor = '#F6F6F7';
          this.style.borderColor = '#E1E3E5';
        });
      });
    }

    async addItemToWishlist(wishlistId, itemData, quantity = null) {
      try {
        if (!itemData) {
          throw new Error('Product data is missing');
        }
        
        if (!wishlistId) {
          throw new Error('Wishlist ID is missing');
        }
        
        if (!this.customerId) {
          throw new Error('Customer ID is missing');
        }
        
        const url = getApiUrl(`/${wishlistId}`, { action: 'add-item' });
        const rawQuantity = quantity !== null ? parseInt(quantity, 10) : (itemData?.quantity || 1);
        
        // Get MOQ from itemData if available, otherwise default to 1
        const moq = getEffectiveMoq(itemData?.moq || itemData?.minimumOrderQuantity || 1);
        const finalQuantity = normalizeQtyWithMoq(rawQuantity, moq).quantity;
        
        // Prepare metadata to include MOQ
        const metadata = {
          ...(itemData?.metadata || {}),
          moq: parseInt(moq) || 1,
          minimum_order_quantity: parseInt(moq) || 1
        };
        
        console.log('[AddToWishlist] Adding item to wishlist:', {
          wishlistId,
          customerId: this.customerId,
          productId: itemData?.productId,
          variantId: itemData?.variantId,
          quantity: finalQuantity,
          metadata
        });
        
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            customerId: this.customerId,
            ...itemData,
            quantity: finalQuantity,
            metadata: metadata
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to add item to wishlist');
        }

        const item = await response.json();
        console.log('[AddToWishlist] Item added successfully:', item);
        
        // Update button state
        if (itemData?.variantId) {
          const button = document.querySelector(`.js-add-to-wishlist[data-variant-id="${itemData.variantId}"]`);
          if (button) {
            button.classList.add('active');
            button.dataset.wishlistId = wishlistId;
          }
        }

        showNotification('Item added to wishlist!');
        try {
          triggerHeaderWishlistPulse();
        } catch (e) {
          console.error('[AddToWishlist] Error triggering header pulse:', e);
        }
        this.currentProductData = null;
        return item;
      } catch (error) {
        console.error('[Wishlist] Error adding item:', error);
        showNotification(error.message || 'Error adding item to wishlist', 'error');
        throw error;
      }
    }

    updateTotals() {
      const itemsListEl = this.container.querySelector('.wishlist-detail__items-list');
      const totalsEl = this.container.querySelector('.wishlist-detail__totals');
      const totalUnitsEl = this.container.querySelector('.wishlist-detail__total-units');
      const subtotalEl = this.container.querySelector('.wishlist-detail__subtotal');
      
      if (!itemsListEl || !totalsEl || !totalUnitsEl || !subtotalEl) return;

      const items = itemsListEl.querySelectorAll('.wishlist-item-row');
      let totalUnits = 0;
      let subtotalCents = 0;

      items.forEach(itemRow => {
        const unitPriceCents = parseInt(itemRow.dataset.unitPriceCents || '0', 10);
        const quantity = parseInt(itemRow.dataset.qty || '1', 10);
        
        totalUnits += quantity;
        subtotalCents += unitPriceCents * quantity;
      });

      totalUnitsEl.textContent = totalUnits.toString();
      const subtotal = subtotalCents / 100;
      subtotalEl.textContent = `$${subtotal.toFixed(2)}`;
      
      if (items.length > 0) {
        totalsEl.classList.remove('hidden');
      } else {
        totalsEl.classList.add('hidden');
      }
    }

    updateItemLineTotal(itemRow) {
      const unitPriceCents = parseInt(itemRow.dataset.unitPriceCents || '0', 10);
      const quantity = parseInt(itemRow.dataset.qty || '1', 10);
      const unitPrice = unitPriceCents / 100;
      const lineTotal = unitPrice * quantity;
      
      const lineTotalEl = itemRow.querySelector('.wishlist-item__line-total');
      if (lineTotalEl && unitPrice > 0) {
        lineTotalEl.textContent = `$${lineTotal.toFixed(2)}`;
      }
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }

  // PDP Wishlist Button with Dropdown functionality
  class PDPWishlistButton {
    constructor(container) {
      this.container = container;
      // Get customer ID from data attribute first (passed from Liquid), then fallback to global
      const rawCustomerId = container.dataset.customerId || CUSTOMER_ID || null;
      
      // Clean up customer ID - remove empty strings and invalid values
      if (rawCustomerId && rawCustomerId !== '' && rawCustomerId !== 'null' && rawCustomerId !== 'undefined') {
        this.customerId = String(rawCustomerId).trim();
        if (this.customerId === '' || this.customerId === 'null' || this.customerId === 'undefined') {
          this.customerId = null;
        }
      } else {
        this.customerId = null;
      }
      
      this.wishlists = [];
      this.selectedWishlists = new Set();
      this.isDropdownOpen = false;
      this.defaultWishlistId = container.dataset.defaultWishlistId || null;
      
      devLog('[PDPWishlistButton] Constructor called');
      
      if (!this.customerId) {
        console.warn('[PDPWishlistButton] ⚠️ Customer ID not found. Customer may not be logged in or ID not passed from Liquid.');
        console.warn('[PDPWishlistButton] All container data attributes:', 
          Array.from(container.attributes)
            .filter(a => a.name.startsWith('data-'))
            .map(a => `${a.name}="${a.value}"`)
            .join(', ')
        );
        // Don't return early - still set up event listeners so we can show errors
      }

      this.init();
      // Don't load wishlists immediately - wait until dropdown is opened to reduce unnecessary API calls
      // Wishlists will be loaded when the dropdown is first opened (in openDropdown method)
    }

    async init() {
      this.setupEventListeners();
      this.setupQuantityInput();
    }

    setupQuantityInput() {
      // Find the quantity input within this container (quantity-input snippet uses name="quantity")
      const quantityInput = this.container.querySelector('input[name="quantity"]');
      const quantityComponent = this.container.querySelector('quantity-input');
      
      if (quantityInput && quantityComponent) {
        console.log('[PDPWishlistButton] Setting up quantity input');
        // Ensure it respects MOQ
        const moq = parseInt(this.container.dataset.moq) || 1;
        quantityInput.min = moq;
        quantityInput.step = moq;
        
        // Set initial value if not set or invalid
        if (!quantityInput.value || parseInt(quantityInput.value) < moq) {
          quantityInput.value = moq;
        }
        
        
        // Listen for quantity changes to ensure it stays within MOQ bounds
        quantityInput.addEventListener('change', () => {
          const currentValue = parseInt(quantityInput.value) || moq;
          if (currentValue < moq) {
            quantityInput.value = moq;
          }
        });
      } else {
        console.warn('[PDPWishlistButton] Quantity input or component not found', { 
          quantityInput: !!quantityInput, 
          quantityComponent: !!quantityComponent,
          allInputs: this.container.querySelectorAll('input')
        });
      }
    }

    async createDefaultWishlist() {
      try {
        const idempotencyKey = `wishlist-default-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        const response = await fetch(getApiUrl(''), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            customerId: this.customerId,
            name: 'My Wishlist',
            description: 'Default wishlist',
            idempotencyKey
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.error || 'Failed to create wishlist');
        }

        const wishlist = await response.json();
        
        // Reload wishlists to include the new one
        await this.loadWishlists();
        
        return wishlist;
      } catch (error) {
        console.error('[PDPWishlistButton] Error creating default wishlist:', error);
        throw error;
      }
    }

    async loadWishlists() {

      if (!this.customerId) {
        console.warn('[PDPWishlistButton] Cannot load wishlists - no customer ID');
        const loadingEl = this.container.querySelector('.pdp-wishlist-dropdown-loading');
        if (loadingEl) {
          loadingEl.textContent = 'Please log in to view wishlists';
          loadingEl.classList.remove('hidden');
        }
        const emptyEl = this.container.querySelector('.pdp-wishlist-dropdown-empty');
        const itemsContainer = this.container.querySelector('.pdp-wishlist-dropdown-items');
        if (emptyEl) emptyEl.classList.add('hidden');
        if (itemsContainer) itemsContainer.classList.add('hidden');
        return;
      }

      // Verify customer ID is valid before proceeding
      if (this.customerId === '' || this.customerId === 'null' || this.customerId === 'undefined') {
        console.warn('[PDPWishlistButton] Invalid customer ID:', this.customerId);
        const loadingEl = this.container.querySelector('.pdp-wishlist-dropdown-loading');
        if (loadingEl) {
          loadingEl.textContent = 'Please log in to view wishlists';
          loadingEl.classList.remove('hidden');
        }
        const emptyEl = this.container.querySelector('.pdp-wishlist-dropdown-empty');
        const itemsContainer = this.container.querySelector('.pdp-wishlist-dropdown-items');
        if (emptyEl) emptyEl.classList.add('hidden');
        if (itemsContainer) itemsContainer.classList.add('hidden');
        return;
      }

      const loadingEl = this.container.querySelector('.pdp-wishlist-dropdown-loading');
      const emptyEl = this.container.querySelector('.pdp-wishlist-dropdown-empty');
      const itemsContainer = this.container.querySelector('.pdp-wishlist-dropdown-items');
      
      // Show loading state
      if (loadingEl) {
        loadingEl.classList.remove('hidden');
        loadingEl.textContent = 'Loading wishlists...';
      }
      if (emptyEl) emptyEl.classList.add('hidden');
      if (itemsContainer) itemsContainer.classList.add('hidden');

      // Add timeout to prevent hanging
      let timeoutId = null;
      
      try {
        // API endpoint expects customer_id as a query parameter
        const url = getApiUrl('', { customer_id: this.customerId });
        
        // Add timeout to prevent hanging (10 seconds)
        const controller = new AbortController();
        timeoutId = setTimeout(() => {
          console.error('[PDPWishlistButton] ⏱️ Request timeout - aborting after 10 seconds');
          controller.abort();
        }, 10000); // 10 second timeout
        
        const response = await fetch(url, {
          method: 'GET',
          headers: { 
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          signal: controller.signal
        });
        
        clearTimeout(timeoutId);
        
        // Read response as text first, then parse it
        const responseText = await response.text();
        
        if (!response.ok) {
          console.error('[PDPWishlistButton] ❌ Error response:', {
            status: response.status,
            statusText: response.statusText,
            text: responseText
          });
          let errorData;
          try {
            errorData = JSON.parse(responseText);
          } catch (e) {
            errorData = { error: responseText || `Server returned ${response.status}` };
          }
          throw new Error(errorData.error || `Failed to load wishlists (${response.status})`);
        }
        
        // Parse successful response
        let data;
        try {
          data = JSON.parse(responseText);
        } catch (parseError) {
          console.error('[PDPWishlistButton] ❌ Failed to parse response as JSON:', parseError);
          console.error('[PDPWishlistButton] Response text that failed to parse:', responseText);
          throw new Error('Invalid response format from server');
        }
        
        this.wishlists = data.wishlists || data || [];
        
        if (!Array.isArray(this.wishlists)) {
          console.error('[PDPWishlistButton] ❌ Expected array but got:', typeof this.wishlists, this.wishlists);
          this.wishlists = [];
        }
        
        this.renderDropdown();
      } catch (error) {
        // Clear timeout if it exists
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        
        console.error('[PDPWishlistButton] ❌ Error loading wishlists:', error);
        console.error('[PDPWishlistButton] Error details:', {
          name: error.name,
          message: error.message,
          stack: error.stack,
          cause: error.cause
        });
        
        let errorMessage = 'Failed to load wishlists. Please try again.';
        
        if (error.name === 'AbortError') {
          console.error('[PDPWishlistButton] Request timed out after 10 seconds');
          errorMessage = 'Request timed out. Please check your connection and try again.';
        } else if (error.message) {
          // Check for common error messages
          if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || error.message.includes('CORS')) {
            errorMessage = 'Network error. Please check your connection and try again.';
          } else if (error.message.includes('404')) {
            errorMessage = 'Wishlist service not found. Please contact support.';
          } else if (error.message.includes('500')) {
            errorMessage = 'Server error. Please try again later.';
          } else if (error.message.includes('401') || error.message.includes('403')) {
            errorMessage = 'Please log in to view wishlists.';
          } else {
            errorMessage = `Error: ${error.message}`;
          }
        }
        
        // Ensure loadingEl is still accessible in catch block (it was declared in try block)
        const loadingElInCatch = this.container.querySelector('.pdp-wishlist-dropdown-loading');
        const emptyElInCatch = this.container.querySelector('.pdp-wishlist-dropdown-empty');
        const itemsContainerInCatch = this.container.querySelector('.pdp-wishlist-dropdown-items');
        
        if (loadingElInCatch) {
          loadingElInCatch.textContent = errorMessage;
          loadingElInCatch.classList.remove('hidden');
        }
        if (emptyElInCatch) emptyElInCatch.classList.add('hidden');
        if (itemsContainerInCatch) itemsContainerInCatch.classList.add('hidden');
      }
    }

    renderDropdown() {
      const itemsContainer = this.container.querySelector('.pdp-wishlist-dropdown-items');
      const loadingEl = this.container.querySelector('.pdp-wishlist-dropdown-loading');
      const emptyEl = this.container.querySelector('.pdp-wishlist-dropdown-empty');
      
      if (!itemsContainer) return;

      if (loadingEl) loadingEl.classList.add('hidden');

      if (this.wishlists.length === 0) {
        if (emptyEl) emptyEl.classList.remove('hidden');
        if (itemsContainer) itemsContainer.classList.add('hidden');
        return;
      }

      if (emptyEl) emptyEl.classList.add('hidden');
      if (itemsContainer) {
        itemsContainer.classList.remove('hidden');
        itemsContainer.innerHTML = this.wishlists.map(w => `
          <label class="pdp-wishlist-dropdown-item flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer rounded" data-wishlist-id="${w.id}">
            <input type="checkbox" 
                   class="pdp-wishlist-checkbox" 
                   value="${w.id}"
                   data-wishlist-id="${w.id}"
                   ${this.selectedWishlists.has(w.id) ? 'checked' : ''}>
            <div class="flex-1">
              <div class="font-medium text-sm">${this.escapeHtml(w.name)}</div>
              <div class="text-xs text-gray-500">${w.itemCount || 0} ${(w.itemCount || 0) === 1 ? 'item' : 'items'}</div>
            </div>
          </label>
        `).join('');
      }

      // Reposition dropdown after content changes (in case height changed)
      if (this.isDropdownOpen) {
        setTimeout(() => this.positionDropdown(), 0);
      }

      // Set default wishlist if none selected and default exists
      if (!this.defaultWishlistId && this.wishlists.length > 0) {
        this.defaultWishlistId = this.wishlists[0].id;
        const buttonText = this.container.querySelector('.js-pdp-wishlist-button-text');
        if (buttonText) {
          buttonText.textContent = `Add to ${this.wishlists[0].name}`;
        }
      } else if (this.defaultWishlistId) {
        const defaultWishlist = this.wishlists.find(w => w.id === this.defaultWishlistId);
        const buttonText = this.container.querySelector('.js-pdp-wishlist-button-text');
        if (buttonText && defaultWishlist) {
          buttonText.textContent = `Add to ${defaultWishlist.name}`;
        }
      }
    }

    setupEventListeners() {
      // Main button click - add to default/current wishlist
      const mainButton = this.container.querySelector('.js-pdp-add-to-wishlist');
      
      if (mainButton) {
        
        // Check if listener already attached (prevent duplicates if init called multiple times)
        if (!mainButton.dataset.listenerAttached) {
          mainButton.dataset.listenerAttached = 'true';
          
          const handleClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('[PDPWishlistButton] Main button clicked!', e);
            this.handleAddToDefaultWishlist();
          };
          
          mainButton.addEventListener('click', handleClick, { capture: false, passive: false });
          
          // Store handler reference for potential cleanup
          mainButton._wishlistClickHandler = handleClick;
          
        }
        
        // Store reference for later use
        this._mainButton = mainButton;
      } else {
        console.error('[PDPWishlistButton] Main button NOT found!');
        console.error('[PDPWishlistButton] All buttons in container:', this.container.querySelectorAll('button'));
        console.error('[PDPWishlistButton] Container HTML (first 1000 chars):', this.container.innerHTML.substring(0, 1000));
      }

      // Dropdown toggle
      const dropdownToggle = this.container.querySelector('.pdp-wishlist-dropdown-toggle');
      const dropdown = this.container.querySelector('.pdp-wishlist-dropdown');
      
      if (dropdownToggle && dropdown) {
        
        // Check if listener already attached (prevent duplicates)
        if (!dropdownToggle.dataset.listenerAttached) {
          dropdownToggle.dataset.listenerAttached = 'true';
          
          const handleToggleClick = (e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('[PDPWishlistButton] Dropdown toggle clicked!', e);
            this.toggleDropdown();
          };
          
          dropdownToggle.addEventListener('click', handleToggleClick, { capture: false, passive: false });
          
          // Store handler reference
          dropdownToggle._wishlistToggleHandler = handleToggleClick;
          
        }
        
        // Store references for later use
        this._dropdownToggle = dropdownToggle;
        this._dropdown = dropdown;

        // Close dropdown when clicking outside
        if (!this._outsideClickHandler) {
          this._outsideClickHandler = (e) => {
            if (!this.container.contains(e.target) && this.isDropdownOpen) {
              this.closeDropdown();
            }
          };
          // Use capture phase and delay to avoid conflicts
          setTimeout(() => {
            document.addEventListener('click', this._outsideClickHandler, true);
          }, 100);
        }
      } else {
        console.error('[PDPWishlistButton] Dropdown elements NOT found!', { 
          dropdownToggle: !!dropdownToggle, 
          dropdown: !!dropdown 
        });
        if (!dropdownToggle) {
          console.error('[PDPWishlistButton] All buttons with class:', this.container.querySelectorAll('.pdp-wishlist-dropdown-toggle'));
        }
        if (!dropdown) {
          console.error('[PDPWishlistButton] All divs with class:', this.container.querySelectorAll('.pdp-wishlist-dropdown'));
        }
      }

      // Checkbox changes - use event delegation (handles dynamic content)
      if (!this._checkboxHandler) {
        this._checkboxHandler = (e) => {
          if (e.target.classList.contains('pdp-wishlist-checkbox')) {
            const wishlistId = e.target.dataset.wishlistId;
            if (e.target.checked) {
              this.selectedWishlists.add(wishlistId);
            } else {
              this.selectedWishlists.delete(wishlistId);
            }
          }
        };
        this.container.addEventListener('change', this._checkboxHandler);
      }

      // Add selected button - use event delegation for dynamically added content
      if (!this._addSelectedHandler) {
        this._addSelectedHandler = (e) => {
          if (e.target.classList.contains('js-pdp-wishlist-add-selected') || 
              e.target.closest('.js-pdp-wishlist-add-selected')) {
            e.preventDefault();
            e.stopPropagation();
            this.handleAddToSelectedWishlists();
          }
        };
        this.container.addEventListener('click', this._addSelectedHandler);
      }
    }

    toggleDropdown() {
      const dropdown = this._dropdown || this.container.querySelector('.pdp-wishlist-dropdown');
      const toggle = this._dropdownToggle || this.container.querySelector('.pdp-wishlist-dropdown-toggle');
      
      if (!dropdown || !toggle) {
        console.warn('[PDPWishlistButton] Cannot toggle dropdown - elements not found', { 
          dropdown: !!dropdown, 
          toggle: !!toggle 
        });
        return;
      }

      if (this.isDropdownOpen) {
        this.closeDropdown();
      } else {
        this.openDropdown();
      }
    }

    openDropdown() {
      const dropdown = this._dropdown || this.container.querySelector('.pdp-wishlist-dropdown');
      const toggle = this._dropdownToggle || this.container.querySelector('.pdp-wishlist-dropdown-toggle');
      
      if (!dropdown || !toggle) {
        console.warn('[PDPWishlistButton] Cannot open dropdown - elements not found', { 
          dropdown: !!dropdown, 
          toggle: !!toggle,
          hasStored: { dropdown: !!this._dropdown, toggle: !!this._dropdownToggle }
        });
        return;
      }

      // Re-check customer ID from data attribute (same cleanup as constructor)
      if (!this.customerId || this.customerId === '' || this.customerId === 'null' || this.customerId === 'undefined') {
        const rawCustomerId = this.container.dataset.customerId;
        if (rawCustomerId && rawCustomerId !== '' && rawCustomerId !== 'null' && rawCustomerId !== 'undefined') {
          this.customerId = String(rawCustomerId).trim();
          if (this.customerId === '' || this.customerId === 'null' || this.customerId === 'undefined') {
            this.customerId = null;
          }
        } else {
          this.customerId = null;
        }
      }

      
      dropdown.classList.remove('hidden');
      dropdown.style.display = 'block';
      dropdown.style.visibility = 'visible';
      dropdown.style.opacity = '1';
      toggle.setAttribute('aria-expanded', 'true');
      this.isDropdownOpen = true;
      
      // Check if there's enough space below and position dropdown accordingly
      this.positionDropdown();
      
      if (this.wishlists.length === 0) {
        if (this.customerId) {
          this.loadWishlists();
        } else {
          console.warn('[PDPWishlistButton] Cannot load wishlists - no customer ID available');
          const loadingEl = this.container.querySelector('.pdp-wishlist-dropdown-loading');
          const emptyEl = this.container.querySelector('.pdp-wishlist-dropdown-empty');
          const itemsContainer = this.container.querySelector('.pdp-wishlist-dropdown-items');
          if (loadingEl) {
            loadingEl.textContent = 'Please log in to view wishlists';
            loadingEl.classList.remove('hidden');
          }
          if (emptyEl) emptyEl.classList.add('hidden');
          if (itemsContainer) itemsContainer.classList.add('hidden');
        }
      } else {
        this.renderDropdown();
      }
      
      // Re-position dropdown after rendering (in case content changed height)
      setTimeout(() => this.positionDropdown(), 0);
    }

    closeDropdown() {
      const dropdown = this._dropdown || this.container.querySelector('.pdp-wishlist-dropdown');
      const toggle = this._dropdownToggle || this.container.querySelector('.pdp-wishlist-dropdown-toggle');
      
      if (!dropdown || !toggle) {
        console.warn('[PDPWishlistButton] Cannot close dropdown - elements not found');
        return;
      }

      dropdown.classList.add('hidden');
      dropdown.style.display = 'none';
      dropdown.style.visibility = 'hidden';
      dropdown.style.opacity = '0';
      toggle.setAttribute('aria-expanded', 'false');
      this.isDropdownOpen = false;
      
      // Remove positioning class when closing
      const wrapper = dropdown.closest('.pdp-wishlist-dropdown-wrapper');
      if (wrapper) {
        wrapper.classList.remove('pdp-wishlist-dropdown-wrapper--above');
      }
      
    }

    positionDropdown() {
      const dropdown = this._dropdown || this.container.querySelector('.pdp-wishlist-dropdown');
      const toggle = this._dropdownToggle || this.container.querySelector('.pdp-wishlist-dropdown-toggle');
      const wrapper = dropdown?.closest('.pdp-wishlist-dropdown-wrapper');
      
      if (!dropdown || !toggle || !wrapper) {
        return;
      }
      
      // Get positions and dimensions
      const toggleRect = toggle.getBoundingClientRect();
      const dropdownHeight = dropdown.offsetHeight || dropdown.scrollHeight;
      const viewportHeight = window.innerHeight;
      
      // Calculate available space below the button
      const spaceBelow = viewportHeight - toggleRect.bottom;
      const spaceAbove = toggleRect.top;
      
      // Estimate dropdown height (use max-height if we don't have actual height yet)
      const estimatedDropdownHeight = dropdownHeight || 400; // max-height from CSS
      const requiredSpace = estimatedDropdownHeight + 8; // margin-top (0.5rem = 8px)
      
      // Position above if not enough space below but enough space above
      if (spaceBelow < requiredSpace && spaceAbove >= requiredSpace) {
        wrapper.classList.add('pdp-wishlist-dropdown-wrapper--above');
      } else {
        wrapper.classList.remove('pdp-wishlist-dropdown-wrapper--above');
      }
    }

    getQuantity() {
      // Quantity input snippet uses name="quantity"
      const quantityInput = this.container.querySelector('input[name="quantity"]');
      const effectiveMoq = getEffectiveMoq(this.container.dataset.moq);
      if (quantityInput) {
        const normalized = normalizeQtyWithMoq(quantityInput.value, effectiveMoq);
        quantityInput.value = String(normalized.quantity);
        return normalized.quantity;
      }
      return effectiveMoq;
    }

    getProductData() {
      const moq = parseInt(this.container.dataset.moq) || 1;
      return {
        productId: this.container.dataset.productId,
        variantId: this.container.dataset.variantId,
        productHandle: this.container.dataset.productHandle,
        productTitle: this.container.dataset.productTitle,
        variantTitle: this.container.dataset.variantTitle || '',
        imageUrl: this.container.dataset.imageUrl || null,
        price: this.container.dataset.price ? parseFloat(this.container.dataset.price) : null,
        quantity: this.getQuantity(),
        moq: moq, // Include MOQ for metadata storage
        minimumOrderQuantity: moq // Also include as alternative key
      };
    }

    async handleAddToDefaultWishlist() {
      
      const mainButton = this.container.querySelector('.js-pdp-add-to-wishlist');
      if (!mainButton) {
        console.warn('[PDPWishlistButton] Main button not found');
        return;
      }

      // Re-check customer ID from data attribute in case it was set after initialization
      if (!this.customerId) {
        const rawCustomerId = this.container.dataset.customerId;
        
        if (rawCustomerId && rawCustomerId !== '' && rawCustomerId !== 'null' && rawCustomerId !== 'undefined') {
          this.customerId = String(rawCustomerId).trim();
          if (this.customerId === '' || this.customerId === 'null' || this.customerId === 'undefined') {
            this.customerId = null;
          }
        } else {
          this.customerId = null;
        }
      }

      if (!this.customerId) {
        console.warn('[PDPWishlistButton] ⚠️ No customer ID found. Customer may not be logged in or ID not passed from Liquid.');
        console.warn('[PDPWishlistButton] Container dataset customerId:', this.container.dataset.customerId);
        showNotification('Please log in to add items to wishlist', 'error');
        // DO NOT redirect to /account/login - this causes issues with Shopify customer portal
        // The button should be hidden if customer is not logged in (handled in Liquid)
        // Just show error notification and return early
        return;
      }

      // If no default wishlist, load wishlists first
      if (!this.defaultWishlistId) {
        if (this.wishlists.length === 0) {
          await this.loadWishlists();
        }
        
        if (this.wishlists.length === 0) {
          // Automatically create a default wishlist instead of asking
          try {
            showNotification('Creating your first wishlist...', 'info');
            const newWishlist = await this.createDefaultWishlist();
            this.defaultWishlistId = newWishlist.id;
            this.wishlists = [newWishlist]; // Update local wishlists array
            showNotification('Wishlist created! Adding item...', 'success');
          } catch (error) {
            console.error('[PDPWishlistButton] Failed to create default wishlist:', error);
            showNotification('Failed to create wishlist. Please try again.', 'error');
            return;
          }
        } else {
          this.defaultWishlistId = this.wishlists[0].id;
          this.renderDropdown();
        }
      }

      const productData = this.getProductData();
      const quantity = this.getQuantity();
      
      // Get MOQ from product data
      const moq = getEffectiveMoq(productData.moq || productData.minimumOrderQuantity || 1);
      const normalizedQuantity = normalizeQtyWithMoq(quantity, moq).quantity;
      
      // Prepare metadata to include MOQ
      const metadata = {
        ...(productData.metadata || {}),
        moq: parseInt(moq) || 1,
        minimum_order_quantity: parseInt(moq) || 1
      };
      

      // Use stored button reference if available, otherwise query
      const buttonToUse = this._mainButton || mainButton;
      
      // Disable button during request
      buttonToUse.disabled = true;
      const textEl = buttonToUse.querySelector('.js-pdp-wishlist-button-text');
      const originalText = textEl?.textContent || 'Add to Wishlist';
      if (textEl) textEl.textContent = 'Adding...';

      try {
        const url = getApiUrl(`/${this.defaultWishlistId}`, { action: 'add-item' });
        
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            customerId: this.customerId,
            ...productData,
            quantity: normalizedQuantity,
            metadata: metadata
          })
        });

        
        if (!response.ok) {
          const errorText = await response.text();
          console.error('[PDPWishlistButton] Error response text:', errorText);
          let errorData;
          try {
            errorData = JSON.parse(errorText);
          } catch (e) {
            errorData = { error: errorText || `Server returned ${response.status}` };
          }
          throw new Error(errorData.error || `Failed to add item to wishlist (${response.status})`);
        }

        const item = await response.json();
        showNotification(`Added to wishlist!`);
        triggerHeaderWishlistPulse();
      } catch (error) {
        console.error('[PDPWishlistButton] Error adding to default wishlist:', error);
        showNotification(error.message || 'Error adding item to wishlist', 'error');
      } finally {
        buttonToUse.disabled = false;
        if (textEl) textEl.textContent = originalText;
      }
    }

    async handleAddToSelectedWishlists() {
      if (this.selectedWishlists.size === 0) {
        showNotification('Please select at least one wishlist', 'error');
        return;
      }

      const addSelectedBtn = this.container.querySelector('.js-pdp-wishlist-add-selected');
      if (!addSelectedBtn) return;

      const productData = this.getProductData();
      const quantity = this.getQuantity();
      const wishlistIds = Array.from(this.selectedWishlists);

      // Disable button during request
      addSelectedBtn.disabled = true;
      const originalText = addSelectedBtn.textContent;
      addSelectedBtn.textContent = 'Adding...';

      let successCount = 0;
      let errorCount = 0;

      // Get MOQ from product data
      const moq = getEffectiveMoq(productData.moq || productData.minimumOrderQuantity || 1);
      const normalizedQuantity = normalizeQtyWithMoq(quantity, moq).quantity;
      
      // Prepare metadata to include MOQ
      const metadata = {
        ...(productData.metadata || {}),
        moq: parseInt(moq) || 1,
        minimum_order_quantity: parseInt(moq) || 1
      };

      for (const wishlistId of wishlistIds) {
        try {
          const url = getApiUrl(`/${wishlistId}`, { action: 'add-item' });
          const response = await fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({
              customerId: this.customerId,
              ...productData,
              quantity: normalizedQuantity,
              metadata: metadata
            })
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.error || 'Failed to add item to wishlist');
          }

          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`[PDPWishlistButton] Error adding to wishlist ${wishlistId}:`, error);
        }
      }

      if (successCount > 0) {
        showNotification(`Added to ${successCount} wishlist${successCount > 1 ? 's' : ''}!`);
        triggerHeaderWishlistPulse();
      }
      if (errorCount > 0) {
        showNotification(`Failed to add to ${errorCount} wishlist${errorCount > 1 ? 's' : ''}`, 'error');
      }

      // Close dropdown and clear selections
      this.closeDropdown();
      this.selectedWishlists.clear();
      const checkboxes = this.container.querySelectorAll('.pdp-wishlist-checkbox');
      checkboxes.forEach(cb => cb.checked = false);

      // Re-enable button
      addSelectedBtn.disabled = false;
      addSelectedBtn.textContent = originalText;
    }

    escapeHtml(text) {
      if (!text) return '';
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }

  // Initialize on DOM ready
  function initWishlist() {
    // Prevent duplicate initialization
    if (window.__wishlistInitialized) {
      return;
    }
    window.__wishlistInitialized = true;
    
    devLog('[Wishlist] Initializing wishlist functionality...');
    
    // Check for PDP wishlist button first (can be on any product page)
    const pdpWishlistWrapper = document.querySelector('.pdp-wishlist-wrapper');
    
    if (pdpWishlistWrapper) {
      if (!pdpWishlistWrapper.dataset.initialized) {
        try {
          pdpWishlistWrapper.dataset.initialized = 'true';
          new PDPWishlistButton(pdpWishlistWrapper);
        } catch (error) {
          console.error('[Wishlist] Error initializing PDPWishlistButton:', error);
          console.error('[Wishlist] Error stack:', error.stack);
        }
      }
    } else {
      console.log('[Wishlist] PDP wishlist wrapper not found on page');
    }
    
    // Check for wishlist detail page
    const wishlistDetail = document.querySelector('.wishlist-detail');
    if (wishlistDetail) {
      // Prevent double initialization (main + fallback)
      if (wishlistDetail.dataset.initialized === 'true') {
        console.log('[Wishlist] WishlistDetail already initialized (skipping)');
        return;
      }
      wishlistDetail.dataset.initialized = 'true';

      console.log('[Wishlist] Found wishlist detail container');
      
      try {
        console.log('[Wishlist] Creating WishlistDetail instance...');
        new WishlistDetail(wishlistDetail);
        console.log('[Wishlist] WishlistDetail initialized successfully');
      } catch (error) {
        console.error('[Wishlist] Error initializing WishlistDetail:', error);
        const loadingEl = wishlistDetail.querySelector('.wishlist-detail__loading');
        if (loadingEl) {
          loadingEl.innerHTML = `<p style="color: #BF0711;">Error initializing wishlist: ${error.message}. Please refresh the page.</p>`;
        }
      }
      return; // Exit early, detail page initialized
    }
    
    // Check for wishlist list page
    const wishlistList = document.querySelector('.wishlist-list');
    if (wishlistList) {
      // Prevent double initialization (main + fallback)
      if (wishlistList.dataset.initialized === 'true') {
        console.log('[Wishlist] WishlistList already initialized (skipping)');
      } else {
        wishlistList.dataset.initialized = 'true';
        
        try {
          console.log('[Wishlist] Creating WishlistList instance...');
          new WishlistList(wishlistList);
          console.log('[Wishlist] WishlistList initialized successfully');
        } catch (error) {
          console.error('[Wishlist] Error initializing WishlistList:', error);
          const loadingEl = wishlistList.querySelector('.wishlist-list__loading');
          if (loadingEl) {
            loadingEl.innerHTML = `<p style="color: #BF0711;">Error initializing wishlist: ${error.message}. Please refresh the page.</p>`;
          }
        }
      }
    }
 
    // Initialize add to wishlist functionality for PLP (product cards) - always check, works on any page
    // Always initialize, even if CUSTOMER_ID is null - customer ID can be read from button data attributes
    // Use event delegation so it works even if buttons are added dynamically
    
    // Check if AddToWishlist is already initialized (prevent double initialization)
    if (!window.__wishlistAddToWishlistInitialized) {
      try {
        new AddToWishlist();
        window.__wishlistAddToWishlistInitialized = true;
      } catch (error) {
        console.error('[Wishlist] Error initializing AddToWishlist:', error);
      }
    }
  }

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      // Small delay to ensure all scripts and DOM elements are ready
      setTimeout(() => {
        initWishlist();
      }, 0);
    });
  } else {
    // Small delay to ensure all scripts and DOM elements are ready
    setTimeout(() => {
      initWishlist();
    }, 0);
  }

  // Safari/iOS performance: Single fallback initialization with proper guard
  // Only initialize if main initialization didn't catch it (for dynamically loaded content)
  setTimeout(() => {
    // Check if already initialized to prevent duplicate execution
    if (window.__wishlistInitialized) {
      return;
    }
    
    const pdpWishlistWrapper = document.querySelector('.pdp-wishlist-wrapper');
    if (pdpWishlistWrapper && !pdpWishlistWrapper.dataset.initialized) {
      const mainButton = pdpWishlistWrapper.querySelector('.js-pdp-add-to-wishlist');
      if (mainButton) {
        pdpWishlistWrapper.dataset.initialized = 'true';
        try {
          new PDPWishlistButton(pdpWishlistWrapper);
        } catch (error) {
          console.error('[Wishlist] Error in fallback PDPWishlistButton initialization:', error);
        }
      }
    }
    
    // Also check for wishlist pages (only if not already initialized)
    if (!window.__wishlistInitialized) {
      const wishlistDetail = document.querySelector('.wishlist-detail');
      const wishlistList = document.querySelector('.wishlist-list');
      
      if (wishlistDetail && !wishlistDetail.dataset.initialized) {
        wishlistDetail.dataset.initialized = 'true';
        initWishlist();
      } else if (wishlistList && !wishlistList.dataset.initialized) {
        wishlistList.dataset.initialized = 'true';
        initWishlist();
      }
    }
  }, 1000);
})();

